import React, { CSSProperties, useCallback, useContext, useEffect, useRef, useState } from 'react';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import PaidIcon from '@mui/icons-material/Paid';
import ReplayIcon from '@mui/icons-material/Replay';
import ChipLogo from 'assets/images/chip.png';
import {
  GOT_HAND_FOR_HAND_ACTIVE,
  GOT_HAND_FOR_HAND_ENDED,
  GOT_TABLE_BREAKING,
  GOT_TABLE_PAUSED,
  GOT_TABLE_RESUMED,
  GOT_WAITING_FOR_TABLES
} from 'constants/eventConstants';
import { DualSocketContext as SocketContext } from 'contexts/DualSocketContext';
import GameContext from 'contexts/GameContext';
import { useGridMode } from 'contexts/GridModeContext';
import { useIsActiveTable } from 'contexts/TableActiveContext';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import Decimal from 'decimal.js';
import { useFeatureFlag } from 'featureFlags';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useIsFreezeout } from 'hooks/useIsFreezeout';
import { findPlayerTournamentRecord, isOutOfTournament } from 'hooks/useLeaveTable';
import { useTableSelector } from 'hooks/useTableSelector';
import { useTournamentTableExit } from 'hooks/useTournamentTableExit';
import { removeTable } from 'reducers/tables';
import useGameService from 'services/GameService';
import { deriveActiveView } from 'utils/activeView';
import {
  clearBalancingMoveTable,
  clearPlayerExitedTable,
  clearRecentlyExitedTable,
  isProtectedTable,
  markBalancingMoveTable,
  markProtectedTable,
  markRecentlyExitedTable
} from 'utils/recentlyExitedTables';

import GameModal, {
  btnRowStyle,
  dangerBtnStyle,
  primaryBtnStyle,
  secondaryBtnStyle
} from 'components/GameModal/GameModal';
import { protectTournamentOrderKey } from 'components/SaloonLobby/useActiveTables';
import { useSitGoResultEntry } from 'components/SitGoResult/useSitGoResult';
import { useSitGoWaiting } from 'components/SitGoWaiting/useSitGoWaiting';

/**
 * A pause / hand-for-hand modal must never pop over a live hand. game.hand is
 * the engine's per-hand phase (GAME_HAND): "" / 0-2 = pre-deal setup, 3-13 =
 * cards in play, 14-16 = paying out / starting the next hand. Only the setup
 * and payout phases are safe "between hands" moments to surface a pause banner.
 */
export const isBetweenHands = (hand: unknown): boolean => {
  if (hand === '' || hand === undefined || hand === null) return true;
  const n = Number(hand);
  return Number.isNaN(n) ? true : n <= 2 || n >= 14;
};

/**
 * EN-11109 AC 5: how long a Sit & Go that has ended for this player holds its
 * interim screens back while the result is on its way (see isSitGoEnding).
 */
export const SIT_GO_INTERIM_HOLD_MS = 15_000;

export type TournamentButtonInputs = {
  tStatus: string;
  pStatus: string;
  isMultiFlightBreak: boolean;
  isFinalMultiFlight: boolean;
  isOnBreak: boolean;
  isRebalancing: boolean;
  isPlayerSeated: boolean;
  canRebuyChips: boolean;
  isShowAddOn: boolean;
  isMaintenance: boolean;
  noOfPlayers: number;
};

export type TournamentButtonOutputs = {
  unregister: boolean;
  close: boolean;
  exitTable: boolean;
  observe: boolean;
  reEntry: boolean;
  addOn: boolean;
};

/**
 * Pure decision function mirroring Unity's TournamentModalButtons (TablesView.cs:1230-1295).
 * Primary action (unregister | close | exitTable) is mutually exclusive; observe / reEntry
 * / addOn are additive. `isRebalancing` is a hard short-circuit — every flag goes false.
 */
export function computeTournamentButtons(input: TournamentButtonInputs): TournamentButtonOutputs {
  if (input.isRebalancing) {
    return {
      unregister: false,
      close: false,
      exitTable: false,
      observe: false,
      reEntry: false,
      addOn: false
    };
  }

  const unregister =
    input.tStatus === 'R' &&
    input.pStatus === 'R' &&
    !input.isMultiFlightBreak &&
    !input.isFinalMultiFlight;

  const close =
    !unregister && (!input.isOnBreak || input.isMultiFlightBreak) && input.pStatus !== 'F';

  const exitTable = !unregister && !close;

  const observe =
    input.pStatus === 'F' &&
    input.tStatus !== 'F' &&
    !input.isMultiFlightBreak &&
    !input.isOnBreak &&
    input.noOfPlayers > 1;

  const reEntry =
    input.canRebuyChips &&
    input.tStatus === 'P' &&
    !input.isPlayerSeated &&
    !input.isMultiFlightBreak &&
    !input.isMaintenance;

  const addOn = input.isShowAddOn && !input.isMaintenance;

  return { unregister, close, exitTable, observe, reEntry, addOn };
}

/**
 * Client-side re-entry eligibility — the same rule the lobby's
 * TournamentDetailModal already uses for its "Re-entry" button, and Unity's
 * TournamentHelper.CanReenter. Mirrored here so the in-table re-entry prompt can
 * fire the instant we know the player busted, instead of waiting ~10-15s for the
 * server's `canRebuyChips` flag to arrive over the (laggy) API-Gateway WS feed.
 *
 * This is OR'd with the server flag at the call site — it never suppresses a
 * server-granted re-entry, only surfaces the prompt earlier. It degrades safely:
 * any missing tournament field makes `hasLateEntry` false → returns false → we
 * fall back to the server flag (today's behavior). And a false positive is
 * bounded: tapping re-entry still hits the server-validated register, which
 * rejects gracefully (the existing register-error modal). MTT-only — the caller
 * gates on tabletype 'm'.
 */
export function computeClientReentryEligible(input: {
  pStatus?: string; // player's tournament status (fast feed): 'F' = busted
  tStatus?: string; // tournament status: 'P' = in progress
  lateEntry?: unknown; // truthy when the tournament allows late entry / re-entry
  lateEntryExpireMins?: number;
  currentLevel?: number;
  isMultiFlight?: number;
  isFinalMultiFlight?: number;
  multiBagOption?: string;
  isQualified?: number;
  isMaintenance?: boolean;
  canRebuyChips?: boolean; // server gotTnmStatus per-player re-entry grant
  isFreezeout?: boolean; // Freezeout tournaments never allow re-entry
}): boolean {
  // A Freezeout never permits re-entry — hard guard over every other signal
  // (including a stray server canRebuyChips), so it can't offer a re-entry the
  // tournament format forbids.
  if (input.isFreezeout) return false;

  const hasLateEntry = !!input.lateEntry && input.tStatus === 'P';
  const lateEntryExpired =
    hasLateEntry && !(Number(input.lateEntryExpireMins) >= Number(input.currentLevel));
  const multiFlightBlock = !!input.isMultiFlight && !!input.isFinalMultiFlight;
  const qualifiedBlock = input.multiBagOption === 'none' && Number(input.isQualified) === 1;

  // The client-side early heuristic: busted, tournament in progress, inside an
  // open late-entry window, not blocked by multi-flight / qualified / maintenance.
  const eligibleByHeuristic =
    input.pStatus === 'F' &&
    input.tStatus === 'P' &&
    hasLateEntry &&
    !lateEntryExpired &&
    !multiFlightBlock &&
    !qualifiedBlock &&
    !input.isMaintenance;

  // Eligible when the server has explicitly granted re-entry (canRebuyChips) OR
  // the early heuristic says so — either way, never for a Freezeout (guarded
  // above). The server flag is an immediate grant; the heuristic surfaces the
  // prompt before that (laggy) flag lands.
  return !!input.canRebuyChips || eligibleByHeuristic;
}

/**
 * Seconds remaining until a scheduled tournament starts, or null when a
 * "starts in" countdown shouldn't be shown.
 *
 * The engine pushes the formatted "Tournament starts in …" message (msg2) but
 * only refreshes it frequently for the player's actively-viewed table (the
 * `page:'game'` registration that the single-table view emits and grid mode
 * deliberately skips). In grid view that message goes stale and the countdown
 * appears frozen. Deriving the remaining seconds locally from
 * `startAtTime - serverTime` — both already kept live in grid (startAtTime via
 * gotTexas, serverTime via the global GOT_SERVER_TIME push) — lets the modal
 * tick the countdown itself in every view.
 *
 * Returns null (caller falls back to the server message) when the tournament
 * is already playing/finished/cancelled, is a Rolling (Sit & Go) table with no
 * fixed start, or the timestamps aren't populated yet.
 *
 * Exported for unit testing.
 */
export function computeStartsInSeconds(input: {
  schedule?: string;
  startAtTime?: number;
  serverTime?: number;
}): number | null {
  const { schedule, startAtTime = 0, serverTime = 0 } = input;
  // `startAtTime - serverTime` is the authoritative time-until-start. We do NOT
  // gate on the table status: the engine flips status to 'P' ~13s before the
  // real start (observed), so a status gate would cut the countdown short. We
  // also don't trust the engine's pushed message text — it freezes (serverMsg2
  // was stuck at "29 seconds"); the timestamps tick correctly instead.
  if (schedule === 'Rolling') return null; // Sit & Go has no fixed start time
  if (startAtTime <= 0 || serverTime <= 0) return null;
  const secondsLeft = startAtTime - serverTime;
  return secondsLeft > 0 ? secondsLeft : null;
}

/**
 * Decide the locally-displayed start countdown. The displayed value is owned
 * by a client-side 1s tick (in the component); this only decides when to
 * SEED it (once) and when to STOP it — it deliberately never re-reads the
 * server value while the countdown is running.
 *
 * Why "seed once then tick": the raw `startAtTime - serverTime` is unstable
 * during the start transition — the global serverTime heartbeat momentarily
 * reports a stale value (a 12s countdown glitching to 36/40s) and the table
 * status briefly churns 'R' → 'P' → 'R', which previously caused a reset +
 * re-seed to the glitchy value. Seeding exactly once and then ticking the
 * client clock down makes the countdown immune to all of that churn.
 *
 *   - `countdownOver` (now, or latched earlier via `alreadyOver`) → stop
 *     permanently (return 0); a later stale value must NOT revive it.
 *   - already running (`prevLocal > 0`) → keep the current value; the tick
 *     owns it, server churn is ignored.
 *   - otherwise seed from the server value the first time it's valid.
 *
 * Exported for unit testing.
 */
export function resolveStartsInDisplay(
  prevLocal: number,
  serverValue: number | null,
  countdownOver: boolean,
  alreadyOver: boolean
): number {
  if (countdownOver || alreadyOver) return 0;
  if (prevLocal > 0) return prevLocal;
  if (serverValue === null || serverValue <= 0) return 0;
  return serverValue;
}

interface PlayerTournament {
  playerId: string;
  status: string;
  position: number;
  prize: number;
  splitPosition: string;
  splitPrize: number;
  finalHandPot: number;
  schedule: string;
  gameID: number;
  game: string;
  endtime: number;
  myRebuys: number;
  isMultiFlightBreak: number;
  startsIn: number;
  breakTime: number;
  tStatus: string;
  time: number;
  gameNo: number;
  noOfPlayer: number;
  isOnBreak: number;
  currentLevel: number;
  rebuyExpireMins: number;
  addOnExpireMins: number;
  isPostBalancing: number;
}

// Every hand-for-hand pause — the tournament-wide gotHandForHandActive and a
// table's per-round barrier wait (HAND_FOR_HAND / FINAL_TABLE_BUBBLE) — is one
// pause from the player's point of view, so they share one acknowledgement.
const HAND_FOR_HAND_PAUSE_KEY = 'HAND_FOR_HAND';
const HAND_FOR_HAND_REASONS = new Set(['HAND_FOR_HAND', 'FINAL_TABLE_BUBBLE']);

/** Identity of a pause for dismissal purposes (EN-10959). */
export const pauseKeyOf = (reason: string, message: string) =>
  HAND_FOR_HAND_REASONS.has(reason) ? HAND_FOR_HAND_PAUSE_KEY : reason || message;

const TournamentScreenModal = () => {
  const tournamentStatus = useTableSelector((table) => table.tournamentStatus);
  // EN-11109: this table has a Sit & Go result (release.sit-go-result-modal
  // on). SitGoResultModal owns the end-of-game screen; this modal steps aside.
  // Always null with the flag off.
  const sitGoResultEntry = useSitGoResultEntry();
  const sitGoResultModal = useFeatureFlag('sitGoResultModal');
  // EN-11257: a Sit & Go still filling, with release.sit-go-seated-waiting on.
  // The felt's waiting panel replaces this modal's pre-start screen (and its
  // Unregister); always null with the flag off.
  const sitGoWaiting = useSitGoWaiting();
  const exitTournamentTable = useTournamentTableExit();
  const { socket, reconnect, connectionStatus } = useContext(SocketContext);
  const [closeModal, setCloseModal] = useState(false);
  // The closed-table prompt is forced open (it ignores showWaitingScreen), so
  // dismissing it needs its own latch. Only a busted player can set it — see
  // canDismissClosedPrompt.
  const [closedPromptDismissed, setClosedPromptDismissed] = useState(false);
  const [showWaitingScreen, setShowWaitingScreen] = useState(false);
  // Tracks pause triggered by gotTablePaused / gotHandForHandActive — independent
  // of game.isPostBalancing (which only covers the standard balancing path).
  const [pausedByTableEvent, setPausedByTableEvent] = useState(false);
  // Dedicated message slots for the event-driven pause. Kept separate from the
  // shared msg1/msg2 (which the gotTnmStatus feed rewrites every push) so the
  // pause text isn't overwritten by — and doesn't flicker against — the
  // server-pushed "Waiting for players to join…" status message.
  const [pauseMsg1, setPauseMsg1] = useState('');
  const [pauseMsg2, setPauseMsg2] = useState('');
  // The pause's REASON code (gotTablePaused `reason`), kept separately from its
  // message. The reason is structured data the server means; the message is
  // copy a product decision can reword at any time — so presentation decisions
  // key off this, never off the text. See isRebalancingLoad.
  const [pauseReason, setPauseReason] = useState('');
  const [playerTournamentDetails, setPlayerTournamentDetails] = useState<PlayerTournament | null>(
    null
  );
  const { unregister, addOn, joinTable, unsubscribeTable } = useGameService();
  const dispatch = useAppDispatch();
  const [observerMode, setObserverMode] = useState(false);
  const {
    setCanRebuyChips,
    canRebuyChips,
    setOpenRebuyChips,
    openRebuyChips,
    showTournamentDetails
  } = useContext(GameContext);
  const { openTable, rebindGridTable, activeTableInfo } = useContext(ViewManagerContext);
  // A pure observer arrives via the lobby's "Observe" (URL ?observe=yes) and never
  // registers, so they never click the in-modal Observe button that sets
  // `observerMode`. Treat the URL observe flag as observing too, so the dead-table
  // recovery below fires for them just like it does for a busted→Observe player.
  const isUrlObserving = activeTableInfo?.observe === 'yes';
  // Multi-table (grid) view is the `/grid` route. When the POV player is
  // balanced onto a new table mid-MTT, the single-table flow navigates to
  // /table/:id — which both joins the new table AND flips the view. In grid
  // view we must NOT navigate (it would eject the player from grid view), but
  // we still have to join the balanced-to table ourselves: grid cells are
  // display-only mirrors that never emit a join, so without this the new table
  // would never enter state.tables and the grid couldn't show it.
  const isGridMode = useGridMode();
  // The balancing timer below reads this at FIRE time (dev review #3821). Its
  // closure captured `isGridMode` from the render that armed it, and the 3s
  // wait is long enough for the view to change underneath it: this instance
  // can re-render with a new value (the tiled carousel's perView crossing 1),
  // or — the reachable shape today — the player enters the grid and this
  // single-view instance is unmounted with its timer still pending. Before
  // this, the stale `false` took the single branch regardless; with
  // forceSingle that branch navigates unconditionally.
  const isGridModeRef = useRef(isGridMode);
  isGridModeRef.current = isGridMode;
  // Is this subtree the table the player is actually looking at? The single-table
  // carousel keeps recently-visited tables mounted (RebindingTableView's
  // keep-alive), so a tournament panel can be live and off-screen at the same
  // time. Read here, used at the render root to scope the rebalancing overlay —
  // see showRebalancingLoad below.
  const isActiveTable = useIsActiveTable();
  const players = useTableSelector((table) => table.players);
  const noOfPlayers = players.filter((player: any) => player !== null && player.GUID !== '').length;
  const guid = useAppSelector((state) => state.user.playerGUID);
  const isMaintenance = useAppSelector((state) => state.maintenanceSchedule.isMaintenance);
  const [isBalancing, setIsBalancing] = useState(false);
  const [isTicketWinner, setIsTicketWinner] = useState(false);

  const isPlayerSeated = useTableSelector((table) => table.currentPlayer.isSeated);
  // Live mirror of seat state, read inside the balancing socket handlers below
  // (which re-bind only on table identity) so it stays current without
  // re-registering the listeners.
  const isSeatedRef = useRef(isPlayerSeated);
  isSeatedRef.current = isPlayerSeated;
  // Latched true when gotTableBreaking fires for THIS table. Secondary "table is
  // dead" signal (game.closed never updates to 1 on the client — a closed table
  // stops sending gotTexas). Reset on table identity change (reset effect below).
  const tableBrokenRef = useRef(false);
  // Set of gameIDs the tournament has CLOSED, sourced from gotTnmStatus's
  // closedGameIDs — the authoritative, repeatedly-pushed signal (observers get
  // gotTnmStatus even after their table dies). Used to know THIS table is dead
  // and to skip closed tables when auto-observing the next one.
  const closedGameIdsRef = useRef<Set<string>>(new Set());
  // The balancing move ("from>to") whose 3 s timer is pending. gotTnmStatus
  // re-announces the move every ~1 s while this instance is still mounted on
  // the source (single view always, and the grid since it keeps the source cell
  // until the destination loads — EN-10625), and each announcement used to
  // schedule a move of its own: repeated joins / opens of the destination.
  const scheduledMoveRef = useRef<string | null>(null);
  // Latch so the auto-open-Tables-list hop (below) fires ONCE per closed table,
  // not on every ~1s gotTnmStatus tick. Without it the picker re-opens every
  // frame, so a player who closes the list to read the "table closed" prompt
  // gets the list slammed back open. Stores the gameID we already opened for;
  // reset on table-identity change.
  const observeHopFiredForRef = useRef<string>('');
  // Render-driving mirror of "is THIS table in the tournament's closedGameIDs".
  // The ref alone can't drive the message text (mutating it doesn't re-render, and
  // reading it during render lags one push behind), so the gotTnmStatus effect
  // sets this state and the closed-table notice appears on the same push.
  const [isThisTableClosed, setIsThisTableClosed] = useState(false);
  const userBalance = useAppSelector((state) => state.user.playerBankAmount);

  const addOnPrice = useTableSelector((table) => table.game.addOnPrice);
  const addOnChips = useTableSelector((table) => table.game.addOnChips);

  const playerGUID = useAppSelector((state) => state.user.playerGUID);
  // canAddOnChips / canRebuyChips arrive in gotTnmStatus (tournamentStatus array),
  // keyed by playerId + tournamentId. Driven by the per-player record below.
  const [canAddOnChips, setCanAddOnChips] = useState(false);
  const [tnmCanRebuyChips, setTnmCanRebuyChips] = useState(false);
  const [remainingAddOn, setRemainingAddOn] = useState(0);

  // Tournament status comes from table.game.status. Read it via its own selector so
  // we can gate Add-On / Re-Entry without depending on `game` being declared first.
  const tournamentStatusCode = useTableSelector((table) => table.game?.status);
  const isTournamentInProgress = tournamentStatusCode === 'P';

  // Tabletype: 'm' = MTT, 't' = SNG, 's' = cash. Re-Entry / Add-On are MTT-only
  // features — Sit & Go busts mean you're out of the tournament, no rebuys.
  const tabletypeCode = useTableSelector((table) => table.game?.tabletype);
  const isMTT = tabletypeCode === 'm';

  // Only offer Add-On while the tournament is actively in progress (status 'P')
  // AND only on MTT tables (SNGs don't support add-ons).
  const isShowAddOn =
    isPlayerSeated &&
    userBalance >= 1 &&
    canAddOnChips &&
    !isMaintenance &&
    isTournamentInProgress &&
    isMTT;

  // Don't default to "Please exit the table" — that message used to leak through
  // whenever tournamentStatus hadn't loaded yet, hiding re-entry/add-on options.
  const [msg1, setMsg1] = useState('');
  const [msg2, setMsg2] = useState('');
  const [msg3, setMsg3] = useState('');
  const [msg4, setMsg4] = useState('');

  const handlePlayerUnregister = () => {
    unregister(game?.tournamentId, game?.gameID);
    handleExitTable();
  };

  const handleExitTable = () => {
    // Close the modal and clear any lingering state BEFORE navigating away.
    // Without this, the existing showWaitingScreen/msg* values can briefly bleed
    // through to the next table the user opens — especially in multi-table mode
    // where the same TournamentScreenModal instance can be rebound to a new
    // gameID before the table-identity reset effect has a chance to run.
    setShowWaitingScreen(false);
    setCloseModal(true);
    setMsg1('');
    setMsg2('');
    setMsg3('');
    setMsg4('');
    setIsBalancing(false);
    setIsTicketWinner(false);
    setObserverMode(false);
    setPausedByTableEvent(false);
    // Leave the table and move on (the next open table, or the lobby) -- the
    // same exit the Sit & Go waiting panel takes after its Unregister. The hook
    // also keeps a left Sit & Go out and, with release.sit-go-result-modal on,
    // lands the last one on the Sit & Go tab with the format just played.
    exitTournamentTable({
      sitGoCapacity: Number((playerTournamentDetails as any)?.maxSeat) || 0
    });
  };

  const handleCloseModal = () => {
    // The X on the bust / info modal (Re-Entry · Observe · Exit) must ONLY
    // dismiss the modal — never navigate. Removing a dead/closed table from
    // "Your Tables" is handled globally by the gotTnmStatus closed-table sweep
    // in App.tsx, so the X doesn't need to do cleanup or send the player to the
    // lobby (that was a reported side effect: X kicked the player to the lobby
    // instead of just closing).
    setShowWaitingScreen(false);
    setCloseModal(true);
  };

  // Dismiss an event-driven pause/HFH modal. Clears the pause flag so the
  // show-effect doesn't immediately re-assert it; a subsequent pause/HFH event
  // re-opens the modal on its own.
  const handleClosePauseModal = () => {
    if (shownPauseKeyRef.current === HAND_FOR_HAND_PAUSE_KEY) {
      handForHandAcknowledgedRef.current = true;
      acknowledgedHfhHandRef.current = game?.gameNo ?? game?.handcount ?? null;
    } else {
      acknowledgedPauseKeyRef.current = shownPauseKeyRef.current;
    }
    setPausedByTableEvent(false);
    setShowWaitingScreen(false);
    setCloseModal(true);
  };

  const game = useTableSelector((table) => table.game);

  // Open the tournament's Tables list so the player can pick a live table to
  // observe — used whenever the current table is dead (closed/broken). The list
  // (TournamentDetails' Tables tab) sources and filters its own active tables,
  // so we don't pick a table here — we just surface the picker.
  const openObserveTablesList = useCallback(() => {
    // Stamp the CLICKING cell's gameID so only that cell's TournamentDetails
    // listener opens its tables list. Without this, two grid cells of the SAME
    // tournament both catch the global event and both pop the dashboard.
    window.dispatchEvent(
      new CustomEvent('open-tournament-tables', { detail: { gameID: game?.gameID } })
    );
  }, [game?.gameID]);

  const handleObserverMode = () => {
    setObserverMode(true);
    setShowWaitingScreen(false);
    // If THIS table has broken (all players moved out), observing it would
    // strand the player on a dead felt. Primary signal: this table's gameID is
    // in gotTnmStatus's closedGameIDs (arrives even after our table dies). Also
    // accept the gotTableBreaking latch, and game.closed as a last resort (it
    // never flips to 1 on the client since a closed table stops sending
    // gotTexas). Jump to another active table in the tournament (or the lobby).
    if (
      closedGameIdsRef.current.has(String(game?.gameID)) ||
      tableBrokenRef.current ||
      Number(game?.closed) === 1
    ) {
      openObserveTablesList();
      return;
    }
    // Re-open this table in observe mode (?observe=yes) so the wrong-table
    // cleanup/redirect in Game.tsx knows the player chose to watch it and won't
    // yank them back to a computed previous table (reported bust→Observe bug).
    // A busted player has no seat, so this only flips the URL observe flag.
    if (game?.gameID) {
      openTable(String(game.gameID), game?.game || 'texas', 'yes');
    }
  };

  const handleRebuyChips = () => {
    setShowWaitingScreen(false);
    setOpenRebuyChips(true);
    setCloseModal(true);
    // Re-entering FROM a closed table: once the server reassigns the player, this
    // old table drops out of their playerTournamentDetails, so it would lose its
    // own-participation protection and the closed-table sweep would removeTable()
    // the cell — closing it instead of smoothly rebinding to the new assigned
    // table. Protect it for the re-entry window so the balancing branch's rebind
    // (source→destination) can transition the cell in place.
    markProtectedTable(String(game?.gameID));
  };

  const handleAddChips = () => {
    addOn();
    setShowWaitingScreen(false);
    setCloseModal(true);
  };

  const [gameID, setGameID] = useState(game?.gameID);

  // Live mirror of the engine hand-phase. The pause/HFH socket handlers below
  // re-bind only on table/tournament identity change, so a value read from the
  // `game` closure would be stale; a ref updated every render is current at
  // event time. `pendingPauseRef` parks a pause event that lands mid-hand so it
  // can be promoted to a visible modal once the hand finishes (see flush effect).
  const handRef = useRef(game?.hand);
  handRef.current = game?.hand;
  const pendingPauseRef = useRef(false);
  // EN-10959: while a table waits, the processor re-sends gotTablePaused for
  // the SAME pause on every poll tick (pauseCheck.js), so a pause the player
  // closed came straight back. `shownPauseKeyRef` names the pause on screen;
  // closing it acknowledges that pause, and repeats of it stay closed for the
  // rest of that wait. An acknowledgement covers one wait, not a whole
  // hand-for-hand period: once the table deals again, the next wait is news
  // and shows once (QA, EN-10959 comment 68293). The two kinds are held apart
  // so closing one does not forget the other; a hand-for-hand acknowledgement
  // also ends with gotHandForHandEnded, any other with gotTableResumed.
  const shownPauseKeyRef = useRef('');
  const handForHandAcknowledgedRef = useRef(false);
  const acknowledgedHfhHandRef = useRef<string | number | null>(null);
  const acknowledgedPauseKeyRef = useRef('');

  // The event releases (gotHandForHandEnded, gotTableResumed) ride the
  // WebSocket, and a dropped connection can lose them. Losing the connection
  // forgets non-HFH acknowledgements. HFH remains acknowledged for this wait;
  // a dealt/new hand or explicit HFH end releases it, not a connection flap.
  useEffect(() => {
    if (connectionStatus === 'disconnected' || connectionStatus === 'reconnecting') {
      acknowledgedPauseKeyRef.current = '';
    }
  }, [connectionStatus]);

  // Pre-start countdown source — derived locally so the "Tournament starts in"
  // timer ticks in grid view too (see computeStartsInSeconds). serverTime is a
  // global, view-independent clock; startAtTime rides on this table's gotTexas.
  // Both can arrive as strings from the engine — coerce so the arithmetic and
  // comparisons below are numeric, not lexical.
  // startAtTime normally rides on this table's gotTexas, which is slow on first
  // load — so the "Tournament starts in" modal used to appear only after
  // gotTexas landed (10–15s late; RCA 20260713 follow-up). Fall back to the
  // registered tournament's startAtTime from the lobby feed (loaded before
  // entry, kept fresh by the always-mounted gotPlayerTournamentDetails listener)
  // so the countdown/modal show immediately. Degrades safely: no confident match
  // → 0 → behaviour is unchanged (modal appears when gotTexas arrives, as today).
  const tableStartAtTime = Number(useTableSelector((table) => table.game?.startAtTime)) || 0;
  const tableGameId = String(game?.gameID ?? '');
  const tableTournId = String(game?.tournamentId ?? '');
  const lobbyStartAtTime = useAppSelector((state) => {
    const lists = [
      ...(((state as any).lobby?.tournament?.tournamentList as any[]) ?? []),
      ...(((state as any).lobby?.tournament?.sitAndGoList as any[]) ?? [])
    ];
    if (!lists.length) return 0;
    const details = ((state as any).lobby?.playerTournamentDetails as any[]) ?? [];
    // Prefer the tournamentId once gotTexas has set it on the table; before that,
    // resolve it from the fast playerTournamentDetails feed by the table gameID.
    let tournId = tableTournId;
    if (!tournId && tableGameId) {
      tournId = String(details.find((d) => String(d?.gameID) === tableGameId)?.tournamentId ?? '');
    }
    // Pre-seat window: the player's per-tournament gameID is still -1 and gotTexas
    // hasn't set game.tournamentId, so neither match above resolves. If the player
    // is registered for exactly ONE tournament (the common case when they just
    // entered its table), use that — its startAtTime is what the countdown needs.
    // GUARD: only when that registration has NO assigned table yet (gameID -1 /
    // empty / 0 = pre-seat). A registration already seated at a CONCRETE, different
    // gameID means the player is viewing some OTHER table — borrowing its
    // startAtTime would show a wrong-tournament countdown. (Any registration whose
    // gameID equals this table would already have matched by gameID above.)
    if (!tournId) {
      const registered = details.filter((d) => d?.status === 'R' || d?.status === 'P');
      if (registered.length === 1) {
        const regGameId = String(registered[0]?.gameID ?? '');
        if (regGameId === '' || regGameId === '-1' || regGameId === '0') {
          tournId = String(registered[0]?.tournamentId ?? '');
        }
      }
    }
    if (!tournId) return 0;
    const match = lists.find((t) => String(t?.tournamentId ?? t?.id) === tournId);
    return Number(match?.startAtTime) || 0;
  });
  const startAtTime = tableStartAtTime || lobbyStartAtTime;
  const serverTime = Number(useAppSelector((state) => state.serverDetails.serverTime)) || 0;

  // Locally-ticked "Tournament starts in" countdown, driven entirely by
  // startAtTime - serverTime (the engine's pushed message text freezes, so we
  // ignore it). `startsInFromServer` is the time remaining while the start is
  // still in the future; `startsInCountdownOver` is the moment it reaches 0.
  const startsInFromServer = computeStartsInSeconds({
    schedule: game?.schedule,
    startAtTime,
    serverTime
  });
  const startsInCountdownOver =
    game?.schedule !== 'Rolling' &&
    startAtTime > 0 &&
    serverTime > 0 &&
    startAtTime - serverTime <= 0;
  const [startsInDisplay, setStartsInDisplay] = useState(0);
  // Latches true once the countdown has reached the start time, so it can't be
  // revived by a stale value afterward. `startsInWasShownRef` records that we
  // displayed a countdown at all (drives the post-start message suppression
  // below). Both reset on table/tournament identity change (reset effect).
  const startsInOverRef = useRef(false);
  const startsInWasShownRef = useRef(false);
  useEffect(() => {
    if (startsInCountdownOver) startsInOverRef.current = true;
    setStartsInDisplay((local) => {
      const next = resolveStartsInDisplay(
        local,
        startsInFromServer,
        startsInCountdownOver,
        startsInOverRef.current
      );
      if (next > 0) startsInWasShownRef.current = true;
      return next;
    });
  }, [startsInFromServer, startsInCountdownOver]);
  const isStartsInTicking = startsInDisplay > 0;
  useEffect(() => {
    if (!isStartsInTicking) return;
    const id = setInterval(() => setStartsInDisplay((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(id);
  }, [isStartsInTicking]);

  // Hard-reset all modal state whenever the user switches to a different table or the
  // current table is balanced onto a new tournament. Without this, msg1/msg2/etc. from
  // a previous tournament (e.g. "Tournament has finished — you won 1st place") can
  // bleed through to a freshly opened MTT/SNG before the new server data arrives.
  // We deliberately re-run on identity change only (not on every render).
  const tableGameID = game?.gameID;
  const tableTournamentId = game?.tournamentId;
  useEffect(() => {
    setMsg1('');
    setMsg2('');
    setMsg3('');
    setMsg4('');
    setShowWaitingScreen(false);
    setCloseModal(false);
    setObserverMode(false);
    setIsBalancing(false);
    setIsTicketWinner(false);
    setPlayerTournamentDetails(null);
    setCanAddOnChips(false);
    setTnmCanRebuyChips(false);
    setRemainingAddOn(0);
    setCanRebuyChips(false);
    setPausedByTableEvent(false);
    pendingPauseRef.current = false;
    shownPauseKeyRef.current = '';
    handForHandAcknowledgedRef.current = false;
    acknowledgedPauseKeyRef.current = '';
    setClosedPromptDismissed(false);
    // …and the pause TEXT with it. A pause belongs to the table it arrived on;
    // carrying its wording to the next table put a stale message on an
    // unrelated modal (see clearPause).
    setPauseMsg1('');
    setPauseMsg2('');
    setPauseReason('');
    // New table → this table hasn't broken (yet); clear the break latch.
    tableBrokenRef.current = false;
    setIsThisTableClosed(false);
    observeHopFiredForRef.current = '';
    // New table / tournament → allow its own pre-start countdown to seed again.
    startsInOverRef.current = false;
    startsInWasShownRef.current = false;
    setStartsInDisplay(0);
  }, [tableGameID, tableTournamentId, setCanRebuyChips]);

  // NOTE: the tournament-feed subscription is NO LONGER driven per-table here.
  // It's centralized in App.tsx (subscribe while ANY open table is in the
  // tournament), because this per-table mount/unmount subscribe kept dropping
  // gotTnmStatus mid-tournament during bust / re-enter / rebalance / observe
  // churn in multi-table view.

  // Listen for balancing/HFH pause events that arrive independently of gotTnmStatus.
  // gotTablePaused  → show modal with server message (bubble, near-money, HFH table pause)
  // gotHandForHandActive → show modal tournament-wide when HFH begins
  // gotHandForHandEnded / gotTableResumed → clear event-driven pause
  // gotWaitingForTables → update msg2 with "Waiting for other tables (n/m)" progress
  useEffect(() => {
    if (!socket || !game?.gameID) return;

    // The balancing broadcaster nests its payload under `data` ({ event, data: {…} });
    // RawWebSocketAdapter strips `event` and forwards the rest, so a handler receives
    // { data: {…} }. Unwrap one level (falling back to the raw object) before reading
    // fields. Without this every field is undefined and the guards below never match.
    const payloadOf = (data: any) => data?.data ?? data ?? {};
    const isThisTable = (p: any) => Number(p?.tableId) === Number(game?.gameID);
    const isThisTournament = (p: any) =>
      !p?.tournamentId || Number(p.tournamentId) === Number(game?.tournamentId);

    // Surface a pause/HFH banner without obscuring a live hand: show it now only
    // if we're already between hands, otherwise park it (pendingPauseRef) and let
    // the flush effect promote it the moment the current hand ends.
    const applyPauseEvent = (message: string, reason = '', key = pauseKeyOf(reason, message)) => {
      // A repeat of a pause the player already closed stays closed.
      const acknowledged =
        key === HAND_FOR_HAND_PAUSE_KEY
          ? handForHandAcknowledgedRef.current
          : key === acknowledgedPauseKeyRef.current;
      if (acknowledged) return;
      shownPauseKeyRef.current = key;
      setPauseMsg1(message);
      setPauseMsg2('');
      setPauseReason(reason);
      if (isBetweenHands(handRef.current)) {
        pendingPauseRef.current = false;
        setPausedByTableEvent(true);
        setShowWaitingScreen(true);
      } else {
        pendingPauseRef.current = true;
      }
    };

    const handleTablePaused = (data: any) => {
      const p = payloadOf(data);
      if (!isThisTable(p)) return;
      applyPauseEvent(p?.message || 'Table paused', String(p?.reason || ''));
    };

    const handleHandForHandActive = (data: any) => {
      const p = payloadOf(data);
      if (!isThisTournament(p)) return;
      applyPauseEvent(p?.message || 'Hand-for-hand play has started', '', HAND_FOR_HAND_PAUSE_KEY);
    };

    // Clearing the MESSAGE, not just the flag, is the point here. pauseMsg1 had
    // exactly one writer and no reset, so it held the last pause's text for the
    // rest of the session — and it feeds displayMsg1 (see the pause branch
    // below), so a resolved pause could still put its wording on an unrelated
    // modal. A pause that has ended has no message.
    const clearPause = () => {
      pendingPauseRef.current = false;
      setPausedByTableEvent(false);
      setPauseMsg1('');
      setPauseMsg2('');
      setPauseReason('');
    };

    const handleTableResumed = (data: any) => {
      const p = payloadOf(data);
      if (!isThisTable(p)) return;
      clearPause();
      // A hand-for-hand wait ends when the table deals again (see the
      // cards-in-play effect), not on a resume, so only a non-HFH
      // acknowledgement ends here.
      acknowledgedPauseKeyRef.current = '';
    };

    const handleHandForHandEnded = (data: any) => {
      const p = payloadOf(data);
      if (!isThisTournament(p)) return;
      clearPause();
      handForHandAcknowledgedRef.current = false;
    };

    const handleWaitingForTables = (data: any) => {
      const p = payloadOf(data);
      const completed = p?.completedTables ?? 0;
      const total = p?.totalTables ?? 0;
      setPauseMsg2(total > 0 ? `Waiting for other tables (${completed}/${total})` : '');
    };

    // gotTableBreaking → THIS table is closing (all players balanced away). A
    // SEATED player is relocated by the balancing flow (isPostBalancing "being
    // moved" modal), so only handle the OBSERVER / busted case here. Latch the
    // break (game.closed won't tell us — a closed table stops sending gotTexas)
    // and, if not seated, open the Tables list so they can pick a live table.
    const handleTableBreaking = (data: any) => {
      const p = payloadOf(data);
      if (!isThisTable(p)) return;
      tableBrokenRef.current = true;
      if (isSeatedRef.current) return;
      openObserveTablesList();
    };

    socket.on(GOT_TABLE_PAUSED, handleTablePaused);
    socket.on(GOT_HAND_FOR_HAND_ACTIVE, handleHandForHandActive);
    socket.on(GOT_TABLE_RESUMED, handleTableResumed);
    socket.on(GOT_HAND_FOR_HAND_ENDED, handleHandForHandEnded);
    socket.on(GOT_WAITING_FOR_TABLES, handleWaitingForTables);
    socket.on(GOT_TABLE_BREAKING, handleTableBreaking);

    return () => {
      socket.off(GOT_TABLE_PAUSED, handleTablePaused);
      socket.off(GOT_HAND_FOR_HAND_ACTIVE, handleHandForHandActive);
      socket.off(GOT_TABLE_RESUMED, handleTableResumed);
      socket.off(GOT_HAND_FOR_HAND_ENDED, handleHandForHandEnded);
      socket.off(GOT_WAITING_FOR_TABLES, handleWaitingForTables);
      socket.off(GOT_TABLE_BREAKING, handleTableBreaking);
    };
  }, [socket, game?.gameID, game?.tournamentId, openObserveTablesList]);

  // gotTnmStatus carries per-player flags for the current tournament:
  //   canAddOnChips, canRebuyChips, myAddons, addOn (max addOns)
  // Read both flags here and mirror canRebuyChips into GameContext so the table-icon
  // re-entry button and RebuyChipsModal stay in sync.
  useEffect(() => {
    if (!Array.isArray(tournamentStatus) || tournamentStatus.length === 0) {
      setCanAddOnChips(false);
      setTnmCanRebuyChips(false);
      return;
    }
    // closedGameIDs: the tournament's list of closed tables. Authoritative "is
    // this table dead?" source — a closed table stops sending gotTexas so
    // game.closed can't tell us, but gotTnmStatus keeps arriving. closedGameIDs is
    // tournament-wide, so read it from ANY record for this tournament. This MUST
    // run before the per-player `myRecord` guard below: a pure observer has no
    // own record, and gating this on myRecord left observers unable to detect a
    // dead table (they'd be stranded on a stalled felt).
    const closedList = (
      tournamentStatus.find(
        (d: any) =>
          Number(d?.tournamentId) === Number(game?.tournamentId) && Array.isArray(d?.closedGameIDs)
      ) as any
    )?.closedGameIDs;
    if (Array.isArray(closedList)) {
      closedGameIdsRef.current = new Set(closedList.map((g: any) => String(g)));
    }
    // Mirror into state so the modal can swap its message the moment THIS table
    // is reported closed (a pure observer gets no other "table is dead" signal).
    setIsThisTableClosed(closedGameIdsRef.current.has(String(game?.gameID)));
    // While watching THIS table — either a busted player who chose Observe
    // (observerMode) or a pure lobby observer (?observe=yes) — if it's now in the
    // tournament's closedGameIDs, hop to the Tables list to pick a live table.
    // Driven by the reliable, repeatedly-pushed closedGameIDs — not just the
    // gotTableBreaking event, which can be missed (e.g. across an idle gap).
    // Gated on observing so it never fires while the bust-decision modal
    // (Re-Entry/Observe/Exit) is still up for a seated-then-busted player.
    if (
      (observerMode || isUrlObserving) &&
      closedGameIdsRef.current.has(String(game?.gameID)) &&
      observeHopFiredForRef.current !== String(game?.gameID)
    ) {
      // Fire once for this closed table. The player can re-open the picker via
      // the Observe button; the hop must not re-slam it open every tick.
      observeHopFiredForRef.current = String(game?.gameID);
      openObserveTablesList();
    }

    // Per-player flags (Add-On / Re-Entry) require the player's own record; a
    // pure observer has none, so clear them and stop here.
    const myRecord = tournamentStatus.find(
      (d: any) => Number(d.tournamentId) === Number(game?.tournamentId) && d.playerId === playerGUID
    ) as any;
    if (!myRecord) {
      setCanAddOnChips(false);
      setTnmCanRebuyChips(false);
      return;
    }
    setCanAddOnChips(myRecord.canAddOnChips === true);
    setTnmCanRebuyChips(myRecord.canRebuyChips === true);
    setRemainingAddOn(+(myRecord.addOn ?? 0) - +(myRecord.myAddons ?? 0));
  }, [
    tournamentStatus,
    playerGUID,
    game?.tournamentId,
    game?.gameID,
    observerMode,
    isUrlObserving,
    openObserveTablesList
  ]);

  // A genuinely-closed table never sends gotTableResumed / gotHandForHandEnded,
  // so a pause / hand-for-hand banner (pausedByTableEvent) latched right before
  // the table broke would stay stuck and MASK the "table closed" prompt (its
  // message + buttons are gated behind !pausedByTableEvent). Once this table is
  // flagged closed (and it's not the balancing destination / mid-move), drop the
  // stale pause so the closed prompt takes over cleanly.
  useEffect(() => {
    const closedPrompt =
      isThisTableClosed && !isProtectedTable(String(game?.gameID)) && !isBalancing;
    if (closedPrompt && pausedByTableEvent) setPausedByTableEvent(false);
  }, [isThisTableClosed, isBalancing, pausedByTableEvent, game?.gameID]);

  // ── Fast re-entry path (client-side) ──────────────────────────────────────
  // The server's `canRebuyChips` rides the laggy API-Gateway feed (gotTnmStatus
  // / gotTexas), so the re-entry prompt can take 10-15s to appear after a bust.
  // The bust itself (status 'F') is known ~1s sooner via the always-on
  // gotPlayerTournamentDetails feed (state.lobby.playerTournamentDetails, kept
  // live in-game by the App-level listener). Compute re-entry eligibility
  // client-side from that fast status + the tournament config already on `game`
  // — the SAME rule the lobby's TournamentDetailModal uses — and OR it with the
  // server flag so the prompt surfaces immediately. Degrades safely: missing
  // config → false → the server flag wins (today's behavior); a false positive
  // is bounded by the server-validated register (existing register-error modal).
  // MTT only.
  const lobbyPlayerTournamentDetails = useAppSelector(
    (state) => state.lobby?.playerTournamentDetails
  );
  const fastBustStatus = (lobbyPlayerTournamentDetails || []).find(
    (d: any) => Number(d?.tournamentId) === Number(game?.tournamentId)
  )?.status;
  // Freezeout format is resolved from the lobby lists (not carried in-game) and
  // passed in as a hard guard — a Freezeout never permits re-entry, even if the
  // server's canRebuyChips were somehow set.
  const isFreezeout = useIsFreezeout(game?.tournamentId);
  const clientReentryEligible = computeClientReentryEligible({
    pStatus: fastBustStatus,
    tStatus: tournamentStatusCode,
    // The late-entry heuristic is MTT-only; neutralize it for non-MTT so only the
    // server's canRebuyChips grant drives their re-entry.
    lateEntry: isMTT ? game?.lateEntry : 0,
    lateEntryExpireMins: game?.lateEntryExpireMins,
    currentLevel: game?.currentLevel,
    isMultiFlight: game?.isMultiFlight,
    isFinalMultiFlight: game?.isFinalMultiFlight,
    multiBagOption: game?.multiBagOption,
    isQualified: game?.isQualified,
    isMaintenance,
    canRebuyChips: tnmCanRebuyChips,
    isFreezeout
  });

  useEffect(() => {
    setCanRebuyChips(clientReentryEligible);
  }, [clientReentryEligible, setCanRebuyChips]);

  useEffect(() => {
    const data = Array.isArray(tournamentStatus) ? tournamentStatus : [];
    const filteredGame = data.filter(
      (d: any) =>
        d.playerId === (guid === '' ? localStorage.getItem('playerGUID') : guid) &&
        Number(d.tournamentId) === Number(game?.tournamentId)
    )[0] as any;

    if (filteredGame !== undefined) {
      const playerDetails: PlayerTournament = filteredGame;
      setMsg1(filteredGame.msg1);
      setMsg2(filteredGame.msg2);
      setMsg3(filteredGame.msg3);
      setMsg4(filteredGame.msg4);

      if (filteredGame.isSatellite && filteredGame.isTicketPrize) {
        setIsTicketWinner(true);
      }
      setPlayerTournamentDetails(playerDetails);

      if (
        gameID !== Number(playerDetails?.gameID) &&
        playerDetails !== undefined &&
        game?.schedule !== 'Rolling' &&
        playerDetails?.gameID !== -1 &&
        playerDetails?.tStatus === 'P' &&
        playerDetails?.isOnBreak === 0 &&
        playerDetails?.status === 'P' &&
        // Do NOT rebalance the player INTO a table the engine has already
        // reported closed in this tick's gotTnmStatus.closedGameIDs list. If we
        // proceed, the destination cell hydrates against a dead slice — the
        // felt renders zeros / BREAK 00:00:00 and stays stuck because no
        // further broadcasts arrive for that gameID (reported bug: after a
        // rebalance the player landed on a table with 0/0 blinds and no seats,
        // and the balancing overlay never cleared). Skipping here lets the
        // next gotPlayerTournamentDetails / gotTnmStatus tick re-announce
        // with a live destination — the engine's usual behaviour when a
        // proposed destination closes concurrently.
        !closedGameIdsRef.current.has(String(playerDetails?.gameID)) &&
        // (Removed) `noOfPlayer > 0` gate — was blocking the legitimate
        // tournament-start / new-table-seed scenario where the engine
        // announces the destination BEFORE anyone has been counted at it
        // (noOfPlayer === 0 until the incoming player joins). The
        // closedGameIDs check above is the authoritative "destination is
        // dead" signal; adding noOfPlayer===0 caught real balances too and
        // stranded the player on the source. If a truly-evacuated
        // destination arrives with a stale closedGameIDs, we'd try to join
        // it — same risk as before that gate was added, which the reports
        // said was acceptable.
        // Normally suppressed while the Re-Entry (rebuy) modal is open so the
        // "being moved" flow doesn't fight it for a SEATED player at THIS table.
        // But when the player has NO live seat here — they're on a closed table,
        // or OBSERVING another table — the rebuy success handler keeps
        // openRebuyChips=true to show its result, which would otherwise block the
        // redirect and leave them stranded (dead felt / stuck observing). In
        // those cases the newly-assigned gameID IS where they must go, so allow
        // the move regardless of the rebuy modal.
        (!openRebuyChips ||
          closedGameIdsRef.current.has(String(game?.gameID)) ||
          observerMode ||
          isUrlObserving)
      ) {
        setMsg1(
          playerDetails.noOfPlayer === 1
            ? 'New Table: Please wait. Opening a new table'
            : 'Balancing Tables: Being moved to another table'
        );
        setIsBalancing(true);
        setShowWaitingScreen(true);

        // Protect the balanced-to table NOW (synchronously, same tick the rebind
        // is armed) so App.tsx's closed-table sweep can't remove it and its
        // gotTexas can't be dropped while the engine's cumulative closedGameIDs
        // transiently lists it as closed. Also clear any stale recently-exited
        // mark on it up front (a player can be balanced BACK to a table they were
        // just moved off). Together these guarantee the destination hydrates and
        // the loading overlay clears — the +3s timer below is now only teardown
        // for the OLD table.
        markProtectedTable(String(playerDetails?.gameID));
        clearRecentlyExitedTable(String(playerDetails?.gameID));
        // …and the player's own earlier exit from it, which would otherwise keep
        // the destination out of the nav and unsubscribed (grid view joins it
        // directly, not through openTable, which is what clears it otherwise).
        clearPlayerExitedTable(String(playerDetails?.gameID));
        // Also protect the OLD (source) table for the duration of the move. The
        // engine flags it closed the instant the player is relocated, so without
        // this the closed-table sweep would removeTable() the source cell
        // immediately — an abrupt disappear instead of the smooth in-place rebind
        // to the new table. The rebind's own teardown (removeTable below, once the
        // destination hydrates) is what actually retires the old cell.
        markProtectedTable(String(game?.gameID));
        // Balancing caption signal — DESTINATION only. The Table felt's chip
        // loader on the destination cell reads this mark to add the "Balancing
        // Tables: Being moved to another table" caption next to the spinner.
        // Deliberately NOT marked for the source: source shows chip only (its
        // rebalance overlay suppresses the caption text below), and Re-Entry
        // doesn't call this at all, so its chip loader stays wordless too.
        markBalancingMoveTable(String(playerDetails?.gameID));
        // Name the destination to the table order NOW, not after the 3s wait:
        // if the new table loads early (App.tsx's auto-resub can subscribe it
        // during the wait), every surface shows it instead of the old one —
        // even while the lobby's details still assign the old seat. Re-armed
        // below, before the teardown.
        protectTournamentOrderKey(game?.tournamentId, String(playerDetails?.gameID));

        // Arm the in-place grid rebind NOW — not after the 3s wait. App.tsx's
        // got_playerGames auto-resub can subscribe the balanced-to table during
        // the wait; without an armed rebind the grid's slot layer would see it
        // arrive "unclaimed" and spawn a NEW cell (the close-old + spawn-new the
        // user saw) before we could rebind. Armed here, the slot layer re-points
        // THIS cell to the new table in place the moment it's known.
        if (isGridMode) {
          rebindGridTable(String(game?.gameID || ''), String(playerDetails?.gameID));
        }

        // One move per announcement, however many ticks repeat it (see
        // scheduledMoveRef). The marks and protections above are idempotent and
        // stay refreshed on every tick.
        const moveKey = `${game?.gameID}>${playerDetails?.gameID}`;
        const moveAlreadyScheduled = scheduledMoveRef.current === moveKey;
        scheduledMoveRef.current = moveKey;
        const completeMove = () => {
          // Settled either way below (moved, or aborted for a re-announce).
          scheduledMoveRef.current = null;
          const previousGameID = String(game?.gameID || '');
          const nextGameID = String(playerDetails?.gameID);
          // Decided HERE, not when the timer was armed (dev review #3821): the
          // closure's `isGridMode` is the view of 3s ago. See isGridModeRef.
          const gridMode = isGridModeRef.current;

          // Late abort: the 3s wait gives the engine time to broadcast a
          // fresh gotTnmStatus, so it's possible the destination has since
          // been reported closed OR emptied in the intervening ticks. Rechecking
          // here — RIGHT before joinTable/openTable — keeps us from committing
          // the navigation to a table that died mid-transition. If we bail,
          // we also undo the marks and reset the balancing state so the next
          // tournamentStatus tick can re-announce a live destination.
          if (closedGameIdsRef.current.has(nextGameID)) {
            clearBalancingMoveTable(nextGameID);
            setIsBalancing(false);
            setShowWaitingScreen(false);
            return;
          }

          // SLOT PROTECTION — both view modes, before any teardown (EN-10857).
          // The shared subscription order keys a tournament table by
          // tournamentId, not gameID (see orderKeyOf in useActiveTables), so
          // this rebalance's destination is ALREADY tracked under the same
          // key the source was — nothing to relink. What's still needed: the
          // destination's GOT_TEXAS is a real network round trip, so a render
          // can land between the source being excluded (markRecentlyExitedTable,
          // below/in the branches under this) and the destination hydrating,
          // with the tournament's key present in neither for a beat. While
          // multi-tabling (other tables open) that beat would otherwise read as
          // "the tournament closed" to the prune and reset the slot — this
          // protects it for a short window so that can't happen. Armed here,
          // before any teardown.
          protectTournamentOrderKey(game?.tournamentId, nextGameID);

          if (gridMode) {
            // Grid view: join the balanced-to table without navigating away from
            // /grid (openTable would eject the player from grid view). Clear any
            // stale recently-exited mark first — in an MTT the player can be
            // balanced BACK to a table they were recently moved off, and without
            // this App.tsx would drop the new table's gotTexas (grace window).
            // The in-place rebind was already armed at balancing detection (see
            // above); here we just make sure the balanced-to table is subscribed
            // (App.tsx's auto-resub usually beats us to it) and clear any stale
            // recently-exited mark so its gotTexas isn't dropped.
            clearRecentlyExitedTable(nextGameID);
            joinTable(nextGameID, playerDetails.game);
          } else {
            // Single-table view: follow the player to their new table via the
            // URL. The route's GameInner emits the join (subscribe) for the new
            // table on mount; explicitly unsubscribe the OLD table here so the
            // teardown is deterministic rather than relying on App.tsx's
            // next-tick got_playerGames diff. Mark it recently-exited so an
            // in-flight broadcast can't resurrect it mid-transition. We do NOT
            // removeTable() the slice here: RebindingTableView cross-fades the
            // old table out first (removing it now would blank that frame), and
            // App.tsx's cleanup drops the stale slice on the next tick.
            if (previousGameID && previousGameID !== nextGameID) {
              markRecentlyExitedTable(previousGameID);
              unsubscribeTable(previousGameID, game?.game || 'texas');
            }
            // forceSingle: a balancing move is a table SWAP, not a new open —
            // the same convention as SlideContainer's swipe switches ("a table
            // switch must never re-route the view"). It matters HERE precisely
            // because of the paragraph above: the old slice is still in
            // state.tables when this runs, so openTable's slot math reads TWO
            // open tables and, with Multi-Table View on and a desktop-wide
            // window, routed a single-table player into grid chrome
            // mid-balancing — the grid's blue navbar over a lone table
            // (EN-10557). Also guards against the count-watcher having flipped
            // the multi-table preference on from that same transient count.
            //
            // …unless the player is on the grid NOW. Entering the grid switches
            // the URL to /grid, which unmounts this single-view instance while
            // its timer is pending — the ref above cannot see past its last
            // render, but the URL can. The always-mounted grid cell for this
            // table armed its own rebind at detection and joins on its own
            // timer, so navigating from here would only yank the player out of
            // the grid they just chose (the regression forceSingle would
            // otherwise introduce: without it, openTable's own slot math sent
            // them to /grid, where they already were). Retiring the old table
            // above is view-independent and has already run.
            //
            // leavingGameID names the seat being left (just unsubscribed
            // above), so it doesn't count toward the table cap while the
            // lobby's details still show it (EN-10421).
            if (deriveActiveView(window.location.pathname, null) !== 'grid') {
              openTable(nextGameID, playerDetails.game, 'no', {
                forceSingle: true,
                leavingGameID: previousGameID
              });
            }
          }

          setGameID(playerDetails?.gameID);
          setShowWaitingScreen(false);
          setCanRebuyChips(false);

          // Grid view only: drop the source table's cell so the grid REPLACES
          // it instead of leaving a stale duplicate alongside the new table.
          // Single-table view doesn't need this — navigating unmounts the old
          // table's GameInner. We deliberately do NOT leaveTable() here: it
          // emits leaveGame=true, which would forfeit the tournament. Balancing
          // only relocates the player's seat, so we just stop the source
          // table's fan-out and remove its slice. Marking it recently-exited
          // keeps App.tsx's auto-resub / any in-flight gotTexas from
          // re-creating the cell.
          if (gridMode && previousGameID && previousGameID !== nextGameID) {
            markRecentlyExitedTable(previousGameID);
            unsubscribeTable(previousGameID, game?.game || 'texas');
            dispatch(removeTable(previousGameID));
          }

          // Balancing arc is functionally complete by this point — join/nav has
          // been issued and the source cell has been retired. Explicitly clear
          // the destination's caption mark so its Table felt overlay hides in
          // sync with the source-side overlay dropping (setShowWaitingScreen
          // just above). Without this we rely on the mark's short auto-expiry;
          // this is the deterministic path.
          clearBalancingMoveTable(nextGameID);
        };
        if (!moveAlreadyScheduled) setTimeout(completeMove, 3000);
      } else {
        setIsBalancing(false);
      }

      // Re-entry eligibility is now driven by the server-pushed table.game.canRebuyChips
      // (synced into GameContext above). No client-side recomputation needed.
    } else {
      const filteredGame = data.filter((d: any) => d.tournamentId === game?.tournamentId)[0] as any;
      if (filteredGame?.tStatus === 'P') {
        setMsg1(filteredGame.msg1);
        setMsg2(filteredGame.msg2);
        setMsg3(filteredGame.msg3);
        setMsg4(filteredGame.msg4);
      } else if (filteredGame?.tStatus === 'F') {
        setMsg1('Tournament has finished');
        setMsg2('Please exit the table');
        setMsg3('');
        setMsg4('');
      }
    }
  }, [tournamentStatus]);

  useEffect(() => {
    if (
      // `!closeModal` on all three, matching every other open term below.
      // These are LEVEL signals, not edges: 'R' holds for the whole pre-start
      // life of a Sit & Go and 'F' holds forever once the tournament ends, and
      // this effect re-runs on game.hand / noOfPlayers / status. Ungated, they
      // re-raised the modal on the very next push after the player closed it,
      // which made the X (and, since EN-10789, the backdrop) cosmetic on the
      // Sit & Go pre-start and "Tournament has finished" screens — it shut and
      // popped straight back. The remaining open triggers below have always
      // carried this guard; these three were the outliers.
      (!closeModal &&
        ((playerTournamentDetails?.status === 'R' && playerTournamentDetails?.tStatus === 'R') ||
          playerTournamentDetails?.tStatus === 'F' ||
          playerTournamentDetails?.tStatus === 'R')) ||
      // Pre-start "Tournament starts in …" modal is deliberately NOT opened
      // on the ticking-countdown signal any more. Product decision: opening
      // the tournament table before start should surface the felt, not a
      // modal covering it. The countdown state is still tracked (so a modal
      // opened for another reason renders the "starts in" text via
      // displayMsg2, and `modalOwnedByLiveState` continues to keep it up
      // when legitimately owned) — only the AUTO-OPEN trigger is removed.
      //
      // Historical note: the trigger was added in an RCA 20260713 follow-up
      // to compensate for a laggy gotTnmStatus feed (the tStatus='R' branch
      // above didn't fire fast enough on table open). If the underlying
      // feed lag re-becomes a UX issue for a different flow, re-open on
      // status transition (a discrete edge) rather than on the countdown
      // being non-zero (a continuous level that fires the moment the table
      // opens, which is the behaviour the modal was covering the felt for).
      // Break modal opens ONLY when the engine has cleared the hand phase
      // (game.hand === ''), signalling that no hand is in progress or being
      // paid out. Any other hand phase (0-2 pre-deal, 3-13 in-play, 14-16
      // payout) is treated as "hand still active" — surfacing the break
      // modal there would interrupt the felt. game.hand is in the effect
      // deps below so this re-evaluates the moment the engine clears the
      // phase, opening the modal at the safe moment.
      (game?.isOnBreak === 1 && game?.hand === '' && !closeModal && !observerMode) ||
      (game?.isMultiFlightBreak === 1 && game?.hand === '' && !closeModal) ||
      // "Waiting for players to join…" — only surface after the engine has
      // cleared the hand phase (game.hand === ''). Without this gate the
      // modal popped over an in-progress showdown / pot payout the moment
      // the table dropped to a single seated player, covering the finish
      // of the hand. game.hand is already in this effect's deps, so the
      // modal opens the moment the engine transitions to the empty phase.
      (noOfPlayers === 1 && !openRebuyChips && !closeModal && game?.hand === '') ||
      game?.isPostBalancing ||
      pausedByTableEvent ||
      ((playerTournamentDetails?.status === 'F' || fastBustStatus === 'F') &&
        (playerTournamentDetails?.tStatus === 'P' || isTournamentInProgress) &&
        tournamentStatusCode !== 'F' &&
        tournamentStatusCode !== 'C' &&
        !observerMode &&
        !closeModal &&
        !openRebuyChips)
      // Busted-at-a-still-running-table prompt (Re-Entry / Observe / Exit).
      // NOTE 1: intentionally NOT gated on `msg1 !== ''`. The bust push carries
      // status:'F' first; the "You've Busted Out." msg1 arrives on a LATER push,
      // so gating on it left a freshly-mounted single-table modal (msg1 '') closed;
      // the header falls back to "You've Busted Out." below until the text lands.
      // NOTE 2: the LOCAL playerTournamentDetails is sourced only from the laggy
      // gotTnmStatus feed (which, post-bust, may stop carrying this player's row at
      // this table until something forces a fresh push — e.g. opening tournament
      // details). So we ALSO accept the fast bust signal `fastBustStatus` from the
      // always-on gotPlayerTournamentDetails feed (state.lobby, line ~840). This is
      // view-agnostic: grid already had the local status, so the OR is a no-op
      // there; single-table now surfaces the prompt without the details detour.
    ) {
      setShowWaitingScreen(true);
    }
  }, [
    canRebuyChips,
    playerTournamentDetails?.status,
    playerTournamentDetails?.tStatus,
    fastBustStatus,
    isTournamentInProgress,
    tournamentStatusCode,
    observerMode,
    game?.hand,
    noOfPlayers,
    playerTournamentDetails?.breakTime,
    game?.isPostBalancing,
    pausedByTableEvent
  ]);

  useEffect(() => {
    if (game?.status === 'P' && !game?.isPostBalancing && !pausedByTableEvent) {
      setShowWaitingScreen(false);
    }
  }, [game?.isPostBalancing, pausedByTableEvent]);

  // Stuck balancing-overlay watchdog. The rebalancing spinner's ONLY dismissal
  // is a later gotTexas carrying isPostBalancing:0, delivered over the
  // API-Gateway WebSocket. If that socket silently half-opens (or the
  // balancing-complete broadcast is dropped) the overlay stays up indefinitely
  // and the still-seated player blinds down with NO signal — the reported
  // final-table lockup. If it is still up after REBALANCE_STUCK_TIMEOUT_MS,
  // force ONE reconnect: fresh sockets + auto-resubscribe let the clearing
  // broadcast arrive, and the ConnectionWarning banner surfaces during the
  // reconnect so the freeze finally becomes visible. This is HARMLESS if
  // balancing is legitimately still in progress — the re-subscribe just
  // re-delivers isPostBalancing:1 and the overlay correctly stays up. Fires once
  // per continuous stuck period (re-arms only when the overlay clears and later
  // reappears), so a genuinely long balance is never spammed with reconnects.
  useEffect(() => {
    if (!showWaitingScreen || !game?.isPostBalancing) return;
    const REBALANCE_STUCK_TIMEOUT_MS = 20000;
    const timer = window.setTimeout(() => {
      // eslint-disable-next-line no-console
      console.warn('[TournamentScreenModal] Balancing overlay stuck — forcing reconnect');
      reconnect();
    }, REBALANCE_STUCK_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [showWaitingScreen, game?.isPostBalancing, reconnect]);

  useEffect(() => {
    if (
      game?.status === 'P' &&
      noOfPlayers > 1 &&
      game?.isOnBreak !== 1 &&
      playerTournamentDetails?.status !== 'F' &&
      !game?.isPostBalancing &&
      !pausedByTableEvent
    ) {
      setShowWaitingScreen(false);
    }
  }, [noOfPlayers]);

  useEffect(() => {
    if (playerTournamentDetails?.status === 'F' && canRebuyChips) {
      setShowWaitingScreen(true);
    }
  }, [canRebuyChips]);

  // Promote a parked pause/HFH banner the instant the live hand finishes. A
  // pause event that arrived mid-hand waits in pendingPauseRef until game.hand
  // reaches a between-hands phase, so the modal surfaces between hands rather
  // than over an active hand.
  useEffect(() => {
    if (pendingPauseRef.current && isBetweenHands(game?.hand)) {
      pendingPauseRef.current = false;
      setPausedByTableEvent(true);
      setShowWaitingScreen(true);
    }
  }, [game?.hand]);

  // Auto-dismiss the event-driven pause once the table starts dealing again.
  // A new hand number is the authoritative "play resumed" signal (bubble burst /
  // table un-paused), so we clear the pause even if the explicit gotTableResumed
  // / gotHandForHandEnded event was missed (reconnect) or hasn't arrived yet.
  const prevHandRef = useRef(game?.hand);
  useEffect(() => {
    if (pausedByTableEvent && game?.hand && game.hand !== prevHandRef.current) {
      setPausedByTableEvent(false);
    }
    prevHandRef.current = game?.hand;
  }, [game?.hand, pausedByTableEvent]);

  // Cards in play end the wait the player acknowledged: the table resumed
  // (even if gotTableResumed was missed), and a hand-for-hand table only deals
  // once its round's barrier clears — no resume event is sent for that. The
  // next wait is a new pause, so it shows once.
  useEffect(() => {
    const handId = game?.gameNo ?? game?.handcount ?? null;
    if (isBetweenHands(game?.hand)) {
      // Reconnect may skip every in-play frame. A newer authoritative hand
      // number still proves the acknowledged wait ended.
      if (
        handForHandAcknowledgedRef.current &&
        handId !== null &&
        acknowledgedHfhHandRef.current !== null &&
        String(handId) !== String(acknowledgedHfhHandRef.current)
      ) {
        handForHandAcknowledgedRef.current = false;
      }
      return;
    }
    handForHandAcknowledgedRef.current = false;
    acknowledgedPauseKeyRef.current = '';
  }, [game?.hand, game?.gameNo, game?.handcount]);

  useEffect(() => {
    if (game?.status === 'P' && !canRebuyChips && !game?.isPostBalancing && !pausedByTableEvent) {
      setShowWaitingScreen(false);
      setCloseModal(false);
      setObserverMode(false);
    }
  }, [game?.status, pausedByTableEvent]);

  useEffect(() => {
    if (game?.status === 'C') {
      setMsg1(
        `Tournament — ${game?.title} has been cancelled. Your refund will be processed as soon as possible. Please contact Hijack Support if you do not receive your refund within 24 hours.`
      );
      setMsg2('');
      setMsg3('');
      setMsg4('');
      setShowWaitingScreen(true);
    }

    // Open the break modal ONLY when the engine has cleared game.hand to ''
    // — the definitive "no hand in progress" signal. Any hand phase (pre-
    // deal 0-2, in-play 3-13, payout 14-16) is treated as active; surfacing
    // the break modal there would interrupt the felt.
    if (game?.status === 'P' && game?.isOnBreak === 1 && game?.hand === '') {
      setShowWaitingScreen(true);
    } else if (
      game?.status === 'P' &&
      (game?.isOnBreak === 0 || closeModal || observerMode) &&
      !game?.isPostBalancing &&
      !pausedByTableEvent &&
      // Do NOT auto-dismiss a busted-out decision modal. When this player's
      // local tournament status is 'F' (or the always-on fast-bust signal
      // is 'F'), they are currently busted and MUST see the Re-Entry /
      // Observe / Exit Table prompt until they interact. Without this
      // guard, adding game.hand to the deps below (needed for the break-
      // modal open gate above) makes this effect fire on every hand
      // transition — silently clearing the bust modal in milliseconds
      // (reported bug: "modal flashes for a split second and dismisses
      // without player interaction").
      playerTournamentDetails?.status !== 'F' &&
      fastBustStatus !== 'F'
    ) {
      setShowWaitingScreen(false);
      if (game?.isOnBreak === 0) {
        setCloseModal(false);
        setObserverMode(false);
      }
    }

    // Same "wait for engine to clear the phase" rule as the primary
    // noOfPlayers gate above — don't pop "Waiting for players to join…"
    // over an in-progress showdown / pot payout. If hand !== '' this
    // branch defers; the primary effect (which has game.hand in its deps)
    // opens the modal the moment the engine transitions to hand ''.
    if (noOfPlayers === 1 && !closeModal && game?.hand === '') {
      setShowWaitingScreen(true);
    }
    // game.hand added so the "hand === ''" break gate above re-evaluates
    // when the engine clears the phase.
  }, [game?.status, game?.isOnBreak, game?.hand, pausedByTableEvent]);

  // ── Break countdown — live "resume in MM:SS" timer ──
  // During a break the server stops ticking levelCountDown locally and instead
  // pushes the remaining break seconds in the tournamentStatus feed. Sync that
  // value into a local state and tick it down 1s/sec so the modal reads smoothly
  // between server pushes.
  const [breakSecondsLeft, setBreakSecondsLeft] = useState(0);
  const breakTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const parseBreakCountdown = (val: any): number => {
    if (typeof val === 'number') return Number.isFinite(val) ? Math.max(0, val) : 0;
    if (typeof val !== 'string' || val === '-' || val === '') return 0;
    const parts = val.split(':').map(Number);
    if (parts.some((n) => Number.isNaN(n))) return 0;
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return 0;
  };

  useEffect(() => {
    if (!Array.isArray(tournamentStatus)) return;
    const match = tournamentStatus.find((d: any) => d.tournamentId === game?.tournamentId) as any;
    if (!match?.levelCountDown) return;
    const serverVal = parseBreakCountdown(match.levelCountDown);
    setBreakSecondsLeft((local) => {
      if (local === 0) return serverVal;
      if (Math.abs(serverVal - local) > 2) return serverVal;
      return local;
    });
  }, [tournamentStatus, game?.tournamentId]);

  useEffect(() => {
    if (game?.isOnBreak !== 1) {
      if (breakTimerRef.current) clearInterval(breakTimerRef.current);
      return;
    }
    breakTimerRef.current = setInterval(() => {
      setBreakSecondsLeft((v) => Math.max(0, v - 1));
    }, 1000);
    return () => {
      if (breakTimerRef.current) clearInterval(breakTimerRef.current);
    };
  }, [game?.isOnBreak]);

  // Determine which buttons to show
  // EN-11311: the player's row alone is not enough. It only updates when a row
  // for this player arrives, so it can still read registered while the table
  // is on a break, and a break only comes once the game has started: no
  // Unregister on the "Tournament on Break" modal.
  const showUnregister =
    playerTournamentDetails?.tStatus === 'R' &&
    playerTournamentDetails?.status === 'R' &&
    game?.isMultiFlightBreak !== 1 &&
    game?.isFinalMultiFlight !== 1 &&
    game?.isOnBreak !== 1;

  // Coerce to boolean — game.isMultiFlightBreak is a number (0/1), so without Boolean()
  // the expression can short-circuit to literal 0 and React renders it as the digit "0".
  const showExitTable = Boolean(
    !showUnregister && (!game?.isOnBreak || game?.isMultiFlightBreak) && !isBalancing
  );

  const showObserve =
    // Accept the fast bust signal too (see the busted-prompt open-trigger note):
    // the local status lags the always-on gotPlayerTournamentDetails feed, so
    // without this the Observe button could be missing on the just-opened prompt.
    (playerTournamentDetails?.status === 'F' || fastBustStatus === 'F') &&
    playerTournamentDetails?.tStatus !== 'F' &&
    game?.isMultiFlightBreak !== 1 &&
    game?.isOnBreak !== 1 &&
    noOfPlayers > 1;

  // Re-Entry only while the tournament is actively in progress. Block when the engine
  // is flagging a multi-flight break (can race ahead of the rebuy window closing).
  // Also block once the tournament has finished — `canRebuyChips` and `game.status`
  // can lag behind `playerTournamentDetails.tStatus`, so check tStatus explicitly to
  // avoid showing Re-Entry on the "Tournament has finished" screen.
  const isTournamentFinished =
    playerTournamentDetails?.tStatus === 'F' || game?.status === 'F' || game?.status === 'C';
  const showReEntry =
    canRebuyChips &&
    isTournamentInProgress &&
    !isTournamentFinished &&
    isMTT &&
    game?.isMultiFlightBreak !== 1 &&
    playerTournamentDetails?.isMultiFlightBreak !== 1;

  // The in-body "Close" button is no longer rendered — closing is handled by
  // GameModal's top-right floating X — so the predicate that used to gate it
  // (`tStatus !== 'F' && isOnBreak === 1 && !multiFlightBreak`) is gone too.

  // Break-aware messaging. When the engine flags `isOnBreak`, the server-pushed
  // msg1 is sometimes a generic "Waiting for players to join..." (it's authored
  // for players who busted / observers, not for seated players). For seated
  // players still in the tournament, override with a break-specific message so
  // they see the expected "Tournament on Break" UI.
  const isOnBreakDisplay =
    game?.isOnBreak === 1 && playerTournamentDetails?.tStatus !== 'F' && !isBalancing;
  const isMultiFlightBreakDisplay =
    game?.isMultiFlightBreak === 1 && playerTournamentDetails?.tStatus !== 'F' && !isBalancing;

  // "This table is closed" recovery prompt. Fires whenever THIS table's gameID
  // is in gotTnmStatus's closedGameIDs and it is NOT the active balancing
  // destination and we're NOT mid balancing move. It deliberately does NOT gate
  // on seat/bust status: a closed table stops sending gotTexas, so isPlayerSeated
  // / player status go stale and can't be trusted — closedGameIDs is the only
  // authoritative "this table is dead" signal. So a seated player whose table
  // dissolved, a player who just BUSTED on it, and a pure observer all reach the
  // same prompt (Re-Entry if eligible / Observe / Exit).
  //   - isProtectedTable: the balancing DESTINATION is transiently in
  //     closedGameIDs mid-reshuffle but is where we're MOVING to — not dead.
  //   - !isBalancing: the OLD table is briefly closed during a relocation; that
  //     window belongs to the spinning "Balancing Tables" overlay, not this.
  //   - !isTournamentFinished: at tournament end, the last table is closed too,
  //     but the correct end-of-tournament message is the "Tournament has
  //     finished" screen (isTournamentCompleteLock), NOT the "This Table's Done
  //     Folded" closed-table prompt — the player has already been eliminated /
  //     placed and there's no Re-Entry or Observe another table anymore.
  //     Reported bug: closed-table modal appearing over a Finished tournament.
  //     (isTournamentFinished is declared above near the Re-Entry gate.)
  const isClosedTablePrompt =
    isThisTableClosed &&
    !isProtectedTable(String(game?.gameID)) &&
    !isBalancing &&
    !isTournamentFinished;
  // Re-entry is offered on the closed prompt only when the player is eligible.
  const reentryEligible = Boolean(canRebuyChips || clientReentryEligible);
  // EN-10959: a BUSTED player may dismiss the closed-table prompt (X or an
  // outside tap), because the felt behind then has its own Exit Table and
  // nobody is stranded. "Busted" here is deliberately useLeaveTable's own
  // signal (isOutOfTournament on the gotTnmStatus record), NOT the faster
  // lobby fastBustStatus: that feed can read 'F' while the record still says
  // active, and in that window Exit Table is hidden — dismissing would strand
  // the player on a dead felt. A registered, still-alive player whose table
  // dissolved keeps the locked prompt — it is their only way out (see
  // closeOnBackdropClick below).
  const exitTournamentRecord = findPlayerTournamentRecord(
    tournamentStatus,
    guid,
    game?.tournamentId
  );
  const canDismissClosedPrompt =
    isClosedTablePrompt && isOutOfTournament(exitTournamentRecord, game?.status);
  useEffect(() => {
    if (!canDismissClosedPrompt) setClosedPromptDismissed(false);
  }, [canDismissClosedPrompt]);
  const handleCloseClosedPrompt = () => setClosedPromptDismissed(true);

  /**
   * The tournament has not begun yet.
   *
   * TWO independent signals, because either alone leaves a window open:
   *
   *  • `startsInDisplay` — the client's own countdown. It needs BOTH
   *    startAtTime and serverTime (see computeStartsInSeconds, which returns
   *    null without them), so it is 0 for the first seconds after a table
   *    opens: startAtTime rides on gotTexas, which the code below notes is
   *    10-15s late on first load, and serverTime is a global heartbeat that
   *    starts at 0. Gating suppression on this alone meant the pre-start modal
   *    leaked in exactly the window where the client could not yet count down
   *    — which is when the player is most likely to be looking (reported
   *    2026-09-09, "Tournament starts in / 33 seconds" still showing).
   *
   *  • `tournamentStatusCode === 'R'` — the engine's own registering state.
   *    Structured, available immediately, and independent of both clocks. But
   *    the engine flips it to 'P' about 13 SECONDS BEFORE the real start (the
   *    reason computeStartsInSeconds deliberately does not gate on status), so
   *    it stops being true slightly early.
   *
   * The countdown covers the last stretch that status gives up on; status
   * covers the opening stretch the countdown cannot compute. Together they
   * span the whole pre-start window.
   *
   * MTT-ONLY, and the `&& isMTT` is load-bearing. 'R' means "registering",
   * which is a TRANSIENT state for an MTT but a STEADY one for a Sit & Go: the
   * engine leaves a Rolling SnG at 'R' for its entire pre-start life and only
   * flips it to 'P' when registeredPlayers === maxSeat (shared/games/texas/
   * tournaments/registration.js). Without the scope, every SnG table was
   * suppressed from the moment the player was routed to it, taking the
   * Unregister button and the engine's own "Waiting for players to join…" /
   * "N more player(s) to start" copy with it — the player could only
   * unregister by going back to Lobby > SnG (reported 2026-09-15).
   *
   * The countdown term needs no such scope: computeStartsInSeconds already
   * returns null for schedule === 'Rolling', so startsInDisplay is always 0
   * on an SnG.
   */
  const isPreStart = startsInDisplay > 0 || (tournamentStatusCode === 'R' && isMTT);

  let displayMsg1 = msg1;
  let displayMsg2 = msg2;
  let displayMsg3 = msg3;
  let displayMsg4 = msg4;
  if (pausedByTableEvent) {
    // Event-driven pause (balancing / near-money / HFH) owns the message while
    // active, so the churning gotTnmStatus msg1 can't flicker through.
    displayMsg1 = pauseMsg1;
    displayMsg2 = pauseMsg2;
    displayMsg3 = '';
    displayMsg4 = '';
  } else if (isClosedTablePrompt) {
    displayMsg1 = "This Table's Done Folded, Partner";
    displayMsg2 =
      "Ain't no more hands bein' dealt here — this table's closed up shop." +
      (reentryEligible
        ? ' Hit Re-Entry to jump right back into the tournament, or Observe another table.'
        : ' Mosey on out and hit Observe to watch another table, or Exit Table to leave.');
    displayMsg3 = '';
    displayMsg4 = '';
  } else if (isMultiFlightBreakDisplay) {
    displayMsg1 = 'Flight Complete';
    displayMsg2 = 'Players have advanced. The next flight will begin shortly.';
    displayMsg3 = '';
    displayMsg4 = '';
  } else if (isOnBreakDisplay) {
    // Title stays "Tournament on Break"; the live countdown sits where msg2 used
    // to be, and the Add-On info block below is rephrased as a "Please note"
    // notice so seated players know exactly what the Add-On button will do.
    displayMsg1 = 'Tournament on Break';
    if (breakSecondsLeft > 0) {
      const mins = Math.floor(breakSecondsLeft / 60);
      const secs = breakSecondsLeft % 60;
      const minPart = mins > 0 ? `${mins} ${mins === 1 ? 'min' : 'mins'}` : '';
      const secPart = secs > 0 ? `${secs} ${secs === 1 ? 'second' : 'seconds'}` : '';
      const parts = [minPart, secPart].filter(Boolean).join(' ');
      displayMsg2 = `will resume in ${parts}`;
    } else {
      displayMsg2 = 'resuming shortly...';
    }
    displayMsg3 = '';
    displayMsg4 = '';
  } else if (
    (playerTournamentDetails?.status === 'F' || fastBustStatus === 'F') &&
    (playerTournamentDetails?.tStatus === 'P' || isTournamentInProgress) &&
    tournamentStatusCode !== 'F' &&
    tournamentStatusCode !== 'C' &&
    !isThisTableClosed &&
    !isBalancing
  ) {
    // Busted at a still-running table (not the closed-table prompt, which has its
    // own copy above). The engine's "You've Busted Out." header (msg1) arrives on
    // a later push than the status:'F' that opens the modal, so fall back to it
    // until then — otherwise the Re-Entry / Observe / Exit prompt flashes with no
    // title. View-agnostic: in grid msg1 is already populated, so msg1 wins.
    displayMsg1 = msg1 || "You've Busted Out.";
  } else if (isPreStart && !isBalancing) {
    // Pre-start "Tournament starts in" countdown is deliberately NOT
    // surfaced. Product decision: opening a tournament table before the
    // start time should show the felt, not a modal. Leave displayMsg1 /
    // displayMsg2 empty here — the suppression guard below (modalHasContent
    // check) will hide the shell entirely when no other reason owns it.
    //
    // Kept as an else-if branch (rather than deleted) so the pre-start state
    // still short-circuits the "engine's stale msg1 leaks in" branches
    // further down — displayMsg1 is seeded from the engine's msg1 above, so
    // without this the server's own 'Tournament starts in' / '29 seconds'
    // renders straight through.
    displayMsg1 = '';
    displayMsg2 = '';
  } else if (
    startsInWasShownRef.current &&
    !isBalancing &&
    game?.status !== 'F' &&
    game?.status !== 'C' &&
    playerTournamentDetails?.tStatus !== 'F'
  ) {
    // The countdown just reached the start time. The engine's "Tournament
    // starts in 29 seconds" message is stale/frozen, so do NOT fall back to it
    // — clear the line until the modal closes (the tournament has begun) or a
    // genuinely different message (break/balancing/finished, handled above)
    // arrives.
    displayMsg1 = '';
    displayMsg2 = '';
  }

  // Closed-table prompt overrides: when THIS table is closed (and it's not the
  // active balancing destination), the normal status-driven button gates go
  // stale (a closed table stops pushing gotTexas), so force the recovery
  // actions — Re-Entry (only when eligible), Observe another table, Exit.
  const showReEntryBtn = showReEntry || (isClosedTablePrompt && reentryEligible);
  const showObserveBtn = showObserve || isClosedTablePrompt;
  const showExitTableBtn = showExitTable || isClosedTablePrompt;

  const hasButtons =
    showUnregister || showExitTableBtn || showObserveBtn || showReEntryBtn || isShowAddOn;

  // Terminal "Tournament has finished" screen: the player has placed/busted and
  // the tournament is over. Gated on showExitTable so the screen always carries
  // a way off the table. Cancelled ('C') keeps its own refund notice.
  //
  // NAME IS NOW HISTORICAL. This used to suppress the X AND the backdrop, so
  // that Exit Table was the only way off a completed table (Unity parity).
  // EN-10789 gave it both back — see the <GameModal> props at the bottom for
  // why that is safe. What the flag still does is mark this screen as one that
  // legitimately OWNS the modal: it exempts it from the pre-start suppression,
  // from the empty-modal suppression, and from the stuck-modal watchdog
  // (modalOwnedByLiveState), so a dismissible finish screen is not the same
  // thing as a screen that can be cleared out from under the player.
  const isTournamentCompleteLock =
    !pausedByTableEvent &&
    showExitTable &&
    (playerTournamentDetails?.tStatus === 'F' || game?.status === 'F');

  // Final-table rebalancing is a transient "seating you" state, not a decision
  // point — Unity shows it as a spinning-logo loading screen, NOT a modal with
  // an Exit Table button (which used to flash over the still-loading felt).
  // Detect it from the standard balancing flag or a balancing/rebalancing pause
  // message; hand-for-hand and break pauses keep the normal modal below.
  // Show the spinning-logo overlay (Unity parity) for EVERY balancing move — the
  // standard "being moved to another table" relocation (isBalancing) as well as
  // the server-flagged final-table reshuffle (isPostBalancing). Previously only
  // the latter got the spinner; a standard move showed a plain dismissible modal.
  // Keyed on the pause REASON, never on its message text.
  //
  // This used to match the message, originally on /rebalanc/i. That was narrow
  // enough to be harmless: no pause the server actually sends contained
  // "rebalanc", so a stale pauseMsg1 could never trigger it. EN-10605 renamed
  // the rebalancing copy to "Sit tight, partner…" and the pattern was widened
  // to /rebalanc|sit tight/i to keep matching it — which quietly made it match
  // half the pause feed. PAUSE_MESSAGES (balancing-v2/pauseMessages.js) uses
  // that same phrase for BALANCING, for NEAR_MONEY, and for
  // DEFAULT_PAUSE_MESSAGE — the fallback for every reason without its own
  // entry. Combined with pauseMsg1 persisting after the pause resolved, ONE
  // earlier pause turned every later waiting state into the bare chip spinner:
  // the break modal lost its card AND its Add-On button, and bust-out and
  // tournament-finished rendered as spinners too (reported 2026-09-09).
  //
  // The reason code is what the server MEANS; the message is copy a product
  // decision can reword at any time. Matching `balanc` against the reason keeps
  // the case the message fallback existed for — a balancing pause that arrives
  // without isBalancing / isPostBalancing set — while NEAR_MONEY and
  // FINAL_TABLE_BUBBLE, which share that wording, correctly keep their card.
  const isRebalancingLoad =
    showWaitingScreen &&
    !isClosedTablePrompt &&
    (isBalancing || !!game?.isPostBalancing || /balanc/i.test(pauseReason));

  // RENDER gate for that overlay, deliberately kept separate from the state
  // above (which still feeds modalOwnedByLiveState, so the stuck-modal watchdog
  // keeps its meaning on every panel, focused or not).
  //
  // Unlike every other overlay mounted in pages/Game.tsx's modal host, the
  // rebalancing branch below returns a RAW <div> and returns EARLY — it never
  // reaches the <GameModal> at the bottom of this component, so it never passed
  // through GameModal's useTableScopedOpen and was the one surface in that host
  // with no table-ownership check at all.
  //
  // That host is `position: fixed` and re-asserts `visibility: 'visible'` on
  // every mounted panel, while the carousel parks off-screen panels with
  // `visibility: hidden` + `left: 300%` (RebindingTableView's heldPanelStyle).
  // With no ancestor transform at rest the `left` offset does not apply to a
  // fixed child, and the `visible` re-assertion cancels the parking — so a
  // kept-alive TOURNAMENT panel painted its `position: absolute; inset: 0`
  // overlay against the viewport, blanketing whatever table the player was on.
  // A cash game would be hidden AND pointer-blocked (host z-index sits above the
  // action rail) while an unrelated tournament balanced — action lost to the
  // clock on a real-money hand.
  //
  // A plain `isActiveTable` conjunction, NOT useTableScopedOpen: that hook
  // latches ownership on the rising edge, which is right for a player-initiated
  // open (buy-in, chat — "which table did they open it on?") but wrong here.
  // Balancing is a server push that arms regardless of where the player is
  // looking, so a latch taken while this panel was held would stay false and
  // swallow the overlay when the player swiped BACK to the balancing tournament.
  // Ownership is not in question: the socket handlers already filter on
  // tableId, so this state belongs to this table by construction. All that was
  // missing is "only paint it while this panel is the one on screen".
  //
  // Grid is exempt for the same reason it is exempt in useTableScopedOpen: there
  // the TableActiveProvider is only the ambient-sound gate (only the FOCUSED
  // cell is "active"), so an unfocused cell is still on screen and must keep
  // rendering its overlay.
  //
  // Note isGridMode covers BOTH the /grid route and the tiled carousel
  // (Game.tsx sets it from `perView > 1`), and the tiled carousel DOES park
  // held panels. Skipping the isActiveTable gate is still safe there, but not
  // because every cell is visible — it is because that branch of GameInner
  // mounts this inside `gridCellStyle` (position: relative) with no fixed host
  // re-asserting visibility, so the overlay is contained by its cell AND
  // inherits the parked panel's `visibility: hidden`. That containment is what
  // the single-table branch lacks, which is the whole bug above.
  const showRebalancingLoad = isRebalancingLoad && (isGridMode || isActiveTable);

  // Consolidated "play resumed" dismissal (watchdog for a STUCK waiting modal).
  // showWaitingScreen is raised by several transient triggers (isPostBalancing,
  // noOfPlayers<2, the balancing relocation) that each have their OWN clear effect
  // keyed on a DIFFERENT dependency. During final-table rebalancing those
  // raise/clear signals can arrive out of order — e.g. isPostBalancing flips 0
  // while game.status is momentarily not 'P', so the status-blind clear at
  // [isPostBalancing] never re-runs once status finally becomes 'P' — leaving an
  // empty "Exit Table" modal stuck over a live hand until the player taps X.
  // This effect re-evaluates on EVERY resume-relevant signal and force-dismisses
  // the waiting screen once the table is demonstrably back in live play (a hand is
  // actually being dealt), UNLESS a state that legitimately owns the modal is
  // active. View-agnostic: reads only redux/table state, no grid/orientation gate.
  const modalOwnedByLiveState =
    pausedByTableEvent ||
    isClosedTablePrompt ||
    isTournamentCompleteLock ||
    isRebalancingLoad ||
    isBalancing ||
    isShowAddOn ||
    canRebuyChips ||
    playerTournamentDetails?.status === 'F' ||
    fastBustStatus === 'F' ||
    startsInDisplay > 0;
  useEffect(() => {
    const inLivePlay =
      game?.status === 'P' &&
      !game?.isPostBalancing &&
      game?.isOnBreak !== 1 &&
      game?.isMultiFlightBreak !== 1 &&
      noOfPlayers > 1 &&
      // A hand is actually being dealt/played (not the setup/payout gap) — the
      // authoritative "play resumed" signal, so we never clear over a table that
      // is still genuinely between hands during a balance.
      !isBetweenHands(game?.hand);
    if (inLivePlay && !modalOwnedByLiveState) {
      setShowWaitingScreen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    game?.status,
    game?.hand,
    game?.isPostBalancing,
    game?.isOnBreak,
    game?.isMultiFlightBreak,
    noOfPlayers,
    modalOwnedByLiveState
  ]);

  // EN-11109 AC 5 (release.sit-go-result-modal): no dead interim step before
  // the Sit & Go result. Between this player's Sit & Go ending and their
  // result reaching SitGoResultWatcher (the laggier gotTnmStatus row), this
  // modal would show the bust prompt ("You've Busted Out.", which the fast
  // feed raises first), the lone winner's "Waiting for players to join...", or
  // the bare finished screen. Leave the felt as it is instead. Bounded: if no
  // result arrives within SIT_GO_INTERIM_HOLD_MS, today's screens come back, so
  // a feed that never delivers the row cannot strand the player.
  const isSitGoEnding =
    sitGoResultModal &&
    tabletypeCode === 't' &&
    !sitGoResultEntry &&
    (playerTournamentDetails?.status === 'F' ||
      fastBustStatus === 'F' ||
      tournamentStatusCode === 'F' ||
      playerTournamentDetails?.tStatus === 'F' ||
      (tournamentStatusCode === 'P' && noOfPlayers <= 1));
  const [sitGoInterimLapsed, setSitGoInterimLapsed] = useState(false);
  useEffect(() => {
    if (!isSitGoEnding) {
      setSitGoInterimLapsed(false);
      return;
    }
    const timer = window.setTimeout(() => setSitGoInterimLapsed(true), SIT_GO_INTERIM_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [isSitGoEnding]);

  // Hard guard: never render on a cash table.
  // The parent in Game.tsx already gates on tableType === 'm' || 't', but during
  // table transitions (exit tournament → join cash game) there are frames where the
  // parent guard hasn't re-evaluated yet and the previous showWaitingScreen state
  // bleeds through. Reading tabletype straight off this table's slice and bailing
  // out here ensures the modal physically can't appear on a Cash table.
  const tabletype = game?.tabletype;
  if (tabletype !== 'm' && tabletype !== 't') return null;

  // EN-11109: a Sit & Go result owns the screen — the bust prompt and the
  // "Tournament has finished" screen both give way to SitGoResultModal.
  if (sitGoResultEntry) return null;
  if (isSitGoEnding && !sitGoInterimLapsed) return null;

  // EN-11257 (release.sit-go-seated-waiting): a Sit & Go still filling shows
  // its registrants in their seats and the felt's waiting panel, which carries
  // the Unregister this modal used to (SitGoWaitingPanel) -- so the pre-start
  // screen gives way. sitGoWaiting is null for an MTT, once the game starts,
  // and with the flag off. The states that own the modal whatever the phase
  // (a pause, the closed-table prompt, the finish screen, a balancing move)
  // still surface.
  if (
    sitGoWaiting &&
    !pausedByTableEvent &&
    !isClosedTablePrompt &&
    !isTournamentCompleteLock &&
    !isBalancing &&
    !isRebalancingLoad &&
    !game?.isPostBalancing
  ) {
    return null;
  }

  // Pre-start suppression — MTT ONLY (see isPreStart). Product decision:
  // opening an MTT table before the tournament's start time should show the
  // felt, not the "Tournament starts in …" modal. A Sit & Go has no start
  // time to count down to, so it is never suppressed here: its pre-start
  // modal carries the Unregister button and the fill-progress copy, and is
  // the only in-table way to unregister.
  //
  // Several unrelated open triggers still fire during MTT pre-start
  // (tStatus === 'R' branches, showUnregister making the Unregister button
  // available so `modalHasContent` stays truthy even when the pre-start
  // display block writes empty strings) — the only fully reliable place to
  // suppress is here, at the render root.
  //
  // Legitimate exemptions listed below own the modal even during pre-start
  // and must still surface:
  //   • pausedByTableEvent  — server pushed a pause banner (bubble, HFH,
  //     near-money) that must be shown regardless of countdown state.
  //   • isClosedTablePrompt — table closed decision the player must
  //     interact with.
  //   • isTournamentCompleteLock — end-of-tournament payout/summary.
  //   • isBalancing / isRebalancingLoad — a balancing move is in flight.
  //   • !!game?.isPostBalancing — the structural rebalancing signal.
  //
  // Once the tournament starts (see isPreStart — the countdown has run out AND
  // the engine has left 'R'), this guard stops firing and every other flow
  // (break, waiting for players, bust prompt, etc.) reopens the modal normally.
  if (
    isPreStart &&
    !pausedByTableEvent &&
    !isClosedTablePrompt &&
    !isTournamentCompleteLock &&
    !isBalancing &&
    !isRebalancingLoad &&
    !game?.isPostBalancing
  ) {
    return null;
  }

  // Suppression guard: never render the modal as a bare "Exit Table" with no
  // message content. When showWaitingScreen is up but the message chain
  // resolved empty and no specific screen owns the modal (rebalancing
  // spinner / closed-table prompt / tournament-complete lock), the player
  // would otherwise see just an Exit Table button flashing over the felt —
  // the reported "dead modal" leak. Prefer no modal at all: the felt
  // communicates the transient loading state better than a modal shell
  // with a lone Exit Table button and no context. If the state genuinely
  // needs the player's attention, a subsequent server push (or state flag
  // change) will re-open the modal with real content.
  //
  // Exempted: isClosedTablePrompt and isTournamentCompleteLock. Both are
  // decision-point states owned by the modal even during brief message lag;
  // their message effects populate displayMsg1 elsewhere, but we keep the
  // exemption as belt-and-braces so a lag frame doesn't blank the prompt.
  const modalHasContent =
    !!displayMsg1 ||
    !!displayMsg2 ||
    !!displayMsg3 ||
    !!displayMsg4 ||
    (isTicketWinner && !game?.isOnBreak) ||
    isShowAddOn ||
    pausedByTableEvent ||
    showReEntryBtn ||
    showObserveBtn ||
    showUnregister;
  // showRebalancingLoad, not isRebalancingLoad: on a held panel there is nothing
  // to paint, so bail out here rather than falling through to the <GameModal>
  // below — which would mount only for its own useTableScopedOpen to suppress it,
  // latching that hook's ownership ref to false for a rise this panel never
  // showed.
  if (
    !modalHasContent &&
    !isClosedTablePrompt &&
    !isTournamentCompleteLock &&
    !showRebalancingLoad
  ) {
    return null;
  }

  // Rebalancing: a spinning-logo loading state (Unity parity) instead of a modal
  // — no Exit Table button, so nothing flashes over the felt while the balanced-
  // to table loads. Clears automatically when the pause ends (play resumes).
  if (showRebalancingLoad) {
    // Source-side rebalance overlay. For a standard balancing move (isBalancing
    // true, not isPostBalancing) we want CHIP ONLY here — the "Balancing Tables:
    // Being moved to another table" caption belongs on the DESTINATION cell (see
    // Table felt's balancing-mark caption). Hiding the title text on source
    // avoids the previous double-caption bug where the source's title stacked
    // with a destination overlay. Final Table Balancing (isPostBalancing) keeps
    // its dedicated wording — that's a different scenario, not a source/dest pair.
    const rebalanceTitleText = game?.isPostBalancing
      ? "Sit tight, partner. We're making sure this is the best seat for ya"
      : isBalancing
      ? null
      : displayMsg1 || 'Balancing Tables: Being moved to another table';
    return (
      <div style={rebalanceOverlayStyle} role='status' aria-live='polite'>
        <style>{REBALANCE_SPIN_KEYFRAMES}</style>
        <img src={ChipLogo} alt='' aria-hidden style={rebalanceSpinnerStyle} />
        {rebalanceTitleText && <div style={rebalanceTitleStyle}>{rebalanceTitleText}</div>}
        {displayMsg2 && !isBalancing && <div style={rebalanceSubStyle}>{displayMsg2}</div>}
      </div>
    );
  }

  return (
    <GameModal
      // Closed-table prompt: force it open regardless of the showWaitingScreen
      // tug-of-war (a closed table's stale game.status / noOfPlayer<=1 flips it),
      // EXCEPT while the tournament-details dashboard (the tables list) is open —
      // clicking "Observe Game" opens that list, and the prompt must step aside
      // so the player can pick a table; closing the list (showTournamentDetails
      // →false) brings the prompt back. Non-closed states keep plain
      // showWaitingScreen behavior.
      open={
        isClosedTablePrompt ? !showTournamentDetails && !closedPromptDismissed : showWaitingScreen
      }
      onClose={
        pausedByTableEvent
          ? handleClosePauseModal
          : isClosedTablePrompt
          ? handleCloseClosedPrompt
          : handleCloseModal
      }
      // No X on the closed-table prompt for a player still in the tournament —
      // they must pick Re-Entry / Observe / Exit; dismissing would leave them
      // on a dead felt with no Exit Table. A busted player gets the X
      // (EN-10959, canDismissClosedPrompt). The event-driven pause has no X
      // either: its close control is the in-body "Close" button below.
      //
      // The terminal "Tournament has finished" screen DOES now get an X
      // (EN-10789). That is a deliberate departure from Unity parity, which
      // made Exit Table the only way off a completed table: the ticket's rule
      // is that a modal the player can dismiss should be dismissable every
      // way, and there is no stranding risk here — a finished tournament sets
      // isBustedOrFinished in useLeaveTable, so the felt's own Exit Table
      // button is visible behind this modal (unit-pinned in the contract test).
      showClose={!pausedByTableEvent && (!isClosedTablePrompt || canDismissClosedPrompt)}
      // EN-10789: the backdrop does exactly what this modal's own dismiss
      // control does, state by state — the X on the waiting / break / add-on /
      // bust / tournament-finished screens, and the in-body "Close" on an
      // event-driven pause. Every one of those runs handleCloseModal or
      // handleClosePauseModal, which are pure local-state cancels: none
      // unregisters, buys an add-on, commits a re-entry or leaves the table.
      //
      // The single exception is the closed-table prompt, and it is keyed on
      // isClosedTablePrompt rather than on the complete-lock. Reasons, so this
      // is not quietly re-added:
      //   • none of the reported screens is this prompt — it was never part of
      //     the tap-outside complaint;
      //   • it is the one state with no X at all, so "the backdrop matches the
      //     X" argues AGAINST dismissal here rather than for it;
      //   • dismissing it strands a registered, not-yet-busted player whose
      //     table dissolved. shouldHideExitTableButton('m', true, false) is
      //     true, so that player has no Exit Table button on the felt and this
      //     prompt is their only direct way out. (The tournament-info
      //     dashboard still offers Observe, but not an exit.)
      // Fixing the exit gate for dead tables would be the way to revisit this.
      //
      // EN-10959 narrows the exception to that player: a BUSTED player on the
      // prompt has the felt's Exit Table (isBustedOrFinished), so the
      // stranding reason does not apply and the prompt dismisses like the
      // busted-out modal does.
      closeOnBackdropClick={!isClosedTablePrompt || canDismissClosedPrompt}
      maxWidth='400px'>
      <div style={contentStyle}>
        {/* Messages */}
        {displayMsg1 && <div style={msg1Style}>{displayMsg1}</div>}

        {isTicketWinner && !game?.isOnBreak && (
          <div style={{ textAlign: 'center', margin: '0.5rem 0' }}>
            <LocalActivityIcon style={{ fontSize: '3rem', color: '#FFCF6C' }} />
          </div>
        )}

        {displayMsg2 && <div style={msg2Style}>{displayMsg2}</div>}
        {displayMsg3 && <div style={msgStyle} dangerouslySetInnerHTML={{ __html: displayMsg3 }} />}
        {displayMsg4 && <div style={msgStyle} dangerouslySetInnerHTML={{ __html: displayMsg4 }} />}

        {/* Add-on info — during a break, render as the "Please note" notice
            so the player sees the charge / chips / remaining count before they
            click Add-On. */}
        {isShowAddOn && isOnBreakDisplay && (
          <div style={addOnNoteStyle}>
            <span style={addOnNoteHeadingStyle}>Please note:</span>
            <span style={addOnNoteBodyStyle}>
              By clicking ADD-ON, you will incur a $
              {new Decimal(addOnPrice ?? 0).toDP(2).toNumber()} charge and receive {addOnChips}{' '}
              chips to your stack. You still have {Math.max(0, remainingAddOn)} more add-on(s) left.
            </span>
          </div>
        )}
        {isShowAddOn && !isOnBreakDisplay && (
          <div style={addOnInfoStyle}>
            <span style={{ fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>
              Add-On Available
            </span>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>
              ${new Decimal(addOnPrice ?? 0).toDP(2).toNumber()} for {addOnChips} chips —{' '}
              {Math.max(0, remainingAddOn)} remaining
            </span>
          </div>
        )}

        {/* Action buttons */}
        {pausedByTableEvent && (
          <div style={buttonColumnStyle}>
            <button style={secondaryBtnStyle} onClick={handleClosePauseModal}>
              Close
            </button>
          </div>
        )}
        {!pausedByTableEvent && hasButtons && (
          <div style={buttonColumnStyle}>
            {showReEntryBtn && (
              <button
                style={{ ...primaryBtnStyle, ...iconBtnRowStyle }}
                disabled={isMaintenance}
                onClick={handleRebuyChips}>
                <ReplayIcon style={btnIconStyle} />
                Re-Entry
              </button>
            )}

            {isShowAddOn && (
              <button style={{ ...primaryBtnStyle, ...iconBtnRowStyle }} onClick={handleAddChips}>
                <PaidIcon style={btnIconStyle} />
                Add-On
              </button>
            )}

            {showObserveBtn && (
              <button style={secondaryBtnStyle} onClick={handleObserverMode}>
                Observe Game
              </button>
            )}

            {showUnregister && (
              <button style={dangerBtnStyle} onClick={handlePlayerUnregister}>
                Unregister
              </button>
            )}

            {showExitTableBtn && (
              <button style={secondaryBtnStyle} onClick={handleExitTable}>
                Exit Table
              </button>
            )}
          </div>
        )}
      </div>
    </GameModal>
  );
};

export default TournamentScreenModal;

const contentStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.5rem',
  textAlign: 'center'
};

// ── Rebalancing loading state (Unity parity) ──
// Covers the TABLE it belongs to (centered spinning logo + message; no buttons),
// blocking interaction while the balanced-to table loads.
//
// `position: absolute` (not fixed) is NOT on its own enough to scope this to one
// table, and the previous version of this comment claimed otherwise. It holds in
// grid, where the cell (gridCellStyle) is relative and becomes the containing
// block. It does NOT hold in the single-table carousel: pages/Game.tsx mounts
// this inside a `position: fixed` host, so the containing block is the VIEWPORT
// and `inset: 0` blankets the whole app — including a cash table on another
// panel. Containment there comes from the showRebalancingLoad gate at the render
// root, which is what actually keeps this on its own table. Do not drop that gate
// on the strength of `absolute` alone.
const REBALANCE_SPIN_KEYFRAMES = `@keyframes hj-rebalance-spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`;

const rebalanceOverlayStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 1300,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '1rem',
  padding: '2rem',
  textAlign: 'center',
  background: 'rgba(18, 16, 12, 0.72)'
};

const rebalanceSpinnerStyle: CSSProperties = {
  width: 72,
  height: 72,
  objectFit: 'contain',
  animation: 'hj-rebalance-spin 1.1s linear infinite',
  filter: 'drop-shadow(0 0 12px rgba(233, 140, 38, 0.45))'
};

const rebalanceTitleStyle: CSSProperties = {
  fontSize: '1.05rem',
  fontWeight: 700,
  color: '#fff',
  letterSpacing: '0.02em'
};

const rebalanceSubStyle: CSSProperties = {
  fontSize: '0.85rem',
  color: 'rgba(255, 255, 255, 0.7)'
};

const msg1Style: CSSProperties = {
  fontSize: '0.95rem',
  fontWeight: 700,
  color: 'rgba(255, 255, 255, 0.9)',
  lineHeight: 1.4
};

const msg2Style: CSSProperties = {
  fontSize: '0.85rem',
  color: 'rgba(255, 255, 255, 0.7)',
  lineHeight: 1.4
};

const msgStyle: CSSProperties = {
  fontSize: '0.8rem',
  color: 'rgba(255, 255, 255, 0.6)',
  lineHeight: 1.4,
  wordBreak: 'break-word'
};

const addOnInfoStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  padding: '0.75rem',
  backgroundColor: 'rgba(233, 140, 38, 0.08)',
  border: '1px solid rgba(233, 140, 38, 0.15)',
  borderRadius: '10px',
  width: '100%'
};

const addOnNoteStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.4rem',
  width: '100%',
  textAlign: 'left',
  marginTop: '0.5rem'
};

const addOnNoteHeadingStyle: CSSProperties = {
  fontWeight: 700,
  fontSize: '0.9rem',
  color: '#E04A4A'
};

const addOnNoteBodyStyle: CSSProperties = {
  fontSize: '0.85rem',
  color: 'rgba(255, 255, 255, 0.85)',
  lineHeight: 1.45
};

const buttonColumnStyle: CSSProperties = {
  ...btnRowStyle,
  flexDirection: 'column',
  gap: '0.5rem'
};

const iconBtnRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem'
};

const btnIconStyle: CSSProperties = {
  fontSize: '1.15rem'
};
